import React, { useState, useRef, useEffect, useCallback, startTransition } from 'react';
// Leaflet (for controlled live basemap)
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Download, Trash2, Grid, RotateCw } from 'lucide-react';

/** Base site-plan scale (feet → CSS px at 100% view zoom). Same for every parcel. */
const PLAN_PX_PER_FOOT = 6;

/** Dash–dot–dot–dash–… pattern (long dash, gap, dot, gap, dot, gap) for lot & parcel boundaries */
const PLAN_BOUNDARY_DASH = [14, 4, 2, 4, 2, 4];

/** Setback inside lot line (toward center), all sides — distance in real feet; drawn as % of plan so it scales with zoom */
const SETBACK_FEET = 5;
const SETBACK_LINE_COLOR = '#a33904';
const SETBACK_DASH = '8 6';

/** Must stay fully inside the dashed setback on all sides (matches `PlanSetbackRect`). */
const SETBACK_ENCLOSED_MODULE_IDS = new Set(['raised-bed', 'fruit-tree', 'garden-row']);

/** Circular footprint on the site plan (and PDF preview). */
const ROUND_PLAN_MODULE_TYPES = new Set(['fruit-tree', 'shade-tree']);

/** Ellipse inscribed in the resize bbox (width/height from handles). Uses CSS % radius, not pill-shaped `rounded-full`. */
const ELLIPSE_PLAN_MODULE_TYPES = new Set(['rain-garden']);

/** Tree-related footprints on the live plan (and PDF preview fill). */
const TREE_PLAN_MODULE_TYPES = new Set(['tree-planting', 'fruit-tree', 'shade-tree']);

/** Stacking on the plan: shade above fruit above all other modules (+ roof overlay). */
function planModuleStackZIndex(moduleType, isSelected) {
  let base;
  if (moduleType === 'shade-tree') base = 30;
  else if (moduleType === 'fruit-tree') base = 20;
  else base = 10;
  return base + (isSelected ? 1 : 0);
}

function planModuleDrawOrderTier(moduleType) {
  if (moduleType === 'shade-tree') return 2;
  if (moduleType === 'fruit-tree') return 1;
  return 0;
}

/** Clamp top-left (%) so the module footprint stays inside the 5 ft setback on every edge. */
function clampModuleToFullSetbackRect(widthFt, heightFt, xPercent, yPercent, siteWidthFt, siteLengthFt) {
  const wFtPlan = Math.max(1, Number(siteWidthFt) || 1);
  const lFtPlan = Math.max(1, Number(siteLengthFt) || 1);
  const insetPctW = Math.min((SETBACK_FEET / wFtPlan) * 100, 49);
  const insetPctL = Math.min((SETBACK_FEET / lFtPlan) * 100, 49);
  const widthPercent = (widthFt / wFtPlan) * 100;
  const heightPercent = (heightFt / lFtPlan) * 100;
  const minX = insetPctW;
  const maxX = 100 - insetPctW - widthPercent;
  const minY = insetPctL;
  const maxY = 100 - insetPctL - heightPercent;
  return {
    xPercent: Math.max(minX, Math.min(maxX, xPercent)),
    yPercent: Math.max(minY, Math.min(maxY, yPercent)),
  };
}
const PLAN_ZOOM_MIN = 0.5;
const PLAN_ZOOM_MAX = 3;
// Zoom controls: 10% increments (e.g. 1.00 -> 1.10 -> 1.20 ...)
const PLAN_ZOOM_STEP = 0.1;
// Temporary kill switch: building-footprint feature is disabled everywhere.
const ENABLE_BUILDING_FOOTPRINTS = false;

const clampPlanZoom = (z) => {
  const n = Math.round(z / PLAN_ZOOM_STEP) * PLAN_ZOOM_STEP;
  return Math.min(PLAN_ZOOM_MAX, Math.max(PLAN_ZOOM_MIN, n));
};

/**
 * PDF raster only (html2canvas cloned document): keep chip size/position, but paint an explicit
 * white/gray layer (z-index 0) and text layer (z-index 1) so black labels are not covered by the fill.
 */
function lidApplyPdfLabelLayerSplitInClone(clonedDoc) {
  const root = clonedDoc.querySelector('[data-lid-plan-capture]');
  const win = clonedDoc.defaultView;
  if (!root || !win) return;

  root.querySelectorAll('div.absolute.inset-2.cursor-move').forEach((stack) => {
    if (!stack.classList.contains('flex') || !stack.classList.contains('flex-col')) return;

    Array.from(stack.children).forEach((row) => {
      const cls = row.getAttribute('class') || '';
      const isWhiteChip = cls.includes('bg-white') && cls.includes('bg-opacity-80');
      const isGrayChip = cls.includes('bg-gray-100');
      if (!isWhiteChip && !isGrayChip) return;

      const cs = win.getComputedStyle(row);
      const frag = clonedDoc.createDocumentFragment();
      while (row.firstChild) {
        frag.appendChild(row.firstChild);
      }

      const bg = clonedDoc.createElement('div');
      bg.setAttribute('aria-hidden', 'true');
      Object.assign(bg.style, {
        position: 'absolute',
        inset: '0',
        zIndex: '0',
        pointerEvents: 'none',
        borderRadius: cs.borderRadius,
        backgroundColor: isGrayChip ? 'rgba(243, 244, 246, 0.95)' : 'rgba(255, 255, 255, 0.8)',
      });

      const fg = clonedDoc.createElement('span');
      Object.assign(fg.style, {
        position: 'relative',
        zIndex: '1',
        display: 'block',
        textAlign: cs.textAlign,
        fontSize: cs.fontSize,
        fontWeight: cs.fontWeight,
        lineHeight: cs.lineHeight,
        color: cs.color,
        fontFamily: cs.fontFamily,
        letterSpacing: cs.letterSpacing,
        // PDF only: shift glyphs up; bg div stays inset:0 (white pills unmoved)
        transform: 'translateY(-0.5em)',
      });
      fg.appendChild(frag);

      row.style.position = 'relative';
      row.style.backgroundColor = 'transparent';
      row.style.backgroundImage = 'none';
      row.appendChild(bg);
      row.appendChild(fg);
    });
  });
}

/** Sidebar “Add module” groups (order preserved) */
const MODULE_CATEGORIES = [
  { id: 'rain-water', label: 'Rain & Water' },
  { id: 'stormwater', label: 'Stormwater & Paving' },
  { id: 'vegetation', label: 'Vegetation' },
  { id: 'roofs-energy', label: 'Roofs, Energy & Storage' },
];

// Ordered goal definitions used for grouping modules in the module picker panel.
// These ids must match the project goal ids used by moduleTypes[].projectGoals and the goal checkboxes.
const PROJECT_GOALS = [
  { id: 'food-production', label: 'Food Production' },
  { id: 'habitat-beautification', label: 'Habitat & Beautification' },
  { id: 'stormwater-management', label: 'Stormwater Management' },
  { id: 'waste-recycling', label: 'Waste & Recycling' },
  { id: 'shared-spaces', label: 'Shared Spaces' },
  { id: 'paths-edges', label: 'Paths & Edges' },
  { id: 'clean-energy', label: 'Clean Energy' },
];

const DEV_PLANNER_STORAGE_KEY = 'lid_dev_planner';

/**
 * Development only: land on the planner (second screen) with a stub site so you can iterate on UI without
 * refilling the form. Use either:
 * - URL: http://localhost:3000/?devPlanner  (optional &w=50&l=100 for lot size in ft)
 * - localStorage: localStorage.setItem('lid_dev_planner', '1') then refresh (clear with removeItem)
 */
function readDevPlannerBootstrap() {
  if (typeof window === 'undefined') return null;
  if (process.env.NODE_ENV !== 'development') return null;
  try {
    const q = new URLSearchParams(window.location.search);
    const qp = q.get('devPlanner');
    const queryOn = q.has('devPlanner') && qp !== '0' && qp !== 'false';
    const storageOn = window.localStorage.getItem(DEV_PLANNER_STORAGE_KEY) === '1';
    if (!queryOn && !storageOn) return null;
    return {
      siteWidth: q.get('w') || '50',
      siteLength: q.get('l') || '100',
      projectGoals: PROJECT_GOALS.map((g) => g.id),
    };
  } catch {
    return null;
  }
}

const initialDevPlanner = readDevPlannerBootstrap();

const PLANNER_SESSION_KEY = 'lid_planner_session';
const PLANNER_SESSION_VERSION = 1;

/** Fixed planning margin on material quantities (15%; not exposed in UI). */
const MATERIAL_WASTE_FACTOR = 1.15;

/** Remember the planner screen for this browser tab so a full refresh does not send you back to the form. Cleared when you click BACK. */
function readPlannerSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PLANNER_SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.v !== PLANNER_SESSION_VERSION || !data.siteCreated) return null;
    return data;
  } catch {
    return null;
  }
}

function clearPlannerSession() {
  try {
    sessionStorage.removeItem(PLANNER_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function mergePlannerInitialState(sessionSnap, devSnap) {
  if (sessionSnap?.siteCreated) {
    const mqDefault = {
      'rain-barrel': 0,
      'water-tank': 0,
      'fruit-tree': 0,
      'shade-tree': 0,
      'raised-bed': 0,
      'garden-row': 0,
      'hoop-house': 0,
    };
    return {
      siteWidth: String(sessionSnap.siteWidth ?? ''),
      siteLength: String(sessionSnap.siteLength ?? ''),
      siteCreated: true,
      projectGoals: Array.isArray(sessionSnap.projectGoals) ? sessionSnap.projectGoals : [],
      soilType: sessionSnap.soilType ?? '',
      siteType: sessionSnap.siteType ?? '',
      hasRoof: Boolean(sessionSnap.hasRoof),
      hasUtilities: Boolean(sessionSnap.hasUtilities),
      roofLengthFt: sessionSnap.roofLengthFt ?? '',
      roofWidthFt: sessionSnap.roofWidthFt ?? '',
      roofAreaSqFt: typeof sessionSnap.roofAreaSqFt === 'number' ? sessionSnap.roofAreaSqFt : 0,
      targetRainfallIn: sessionSnap.targetRainfallIn ?? 1,
      pondingDepthIn: sessionSnap.pondingDepthIn ?? 6,
      minRainGardenAreaSqFt: sessionSnap.minRainGardenAreaSqFt ?? null,
      modules: Array.isArray(sessionSnap.modules) ? sessionSnap.modules : [],
      existingStructures: Array.isArray(sessionSnap.existingStructures) ? sessionSnap.existingStructures : [],
      moduleQuantities: { ...mqDefault, ...(sessionSnap.moduleQuantities && typeof sessionSnap.moduleQuantities === 'object' ? sessionSnap.moduleQuantities : {}) },
      northRotation: sessionSnap.northRotation ?? 0,
      planViewZoom: typeof sessionSnap.planViewZoom === 'number' ? sessionSnap.planViewZoom : 1,
      basemapMode: sessionSnap.basemapMode ?? 'none',
      basemapBufferPct: typeof sessionSnap.basemapBufferPct === 'number' ? sessionSnap.basemapBufferPct : 0.5,
      showNorthArrow: Boolean(sessionSnap.showNorthArrow),
      planRotationDeg: sessionSnap.planRotationDeg ?? 0,
      northAngleDeg: sessionSnap.northAngleDeg ?? 0,
      address: typeof sessionSnap.address === 'string' ? sessionSnap.address : '',
      parcelStreetLine: typeof sessionSnap.parcelStreetLine === 'string' ? sessionSnap.parcelStreetLine : '',
    };
  }
  return {
    siteWidth: devSnap?.siteWidth ?? '',
    siteLength: devSnap?.siteLength ?? '',
    siteCreated: Boolean(devSnap),
    projectGoals: devSnap?.projectGoals ?? [],
    soilType: '',
    siteType: '',
    hasRoof: false,
    hasUtilities: false,
    roofLengthFt: '',
    roofWidthFt: '',
    roofAreaSqFt: 0,
    targetRainfallIn: 1,
    pondingDepthIn: 6,
    minRainGardenAreaSqFt: null,
    modules: [],
    existingStructures: [],
    moduleQuantities: {
      'rain-barrel': 0,
      'water-tank': 0,
      'fruit-tree': 0,
      'shade-tree': 0,
      'raised-bed': 0,
      'garden-row': 0,
      'hoop-house': 0,
    },
    northRotation: 0,
    planViewZoom: 1,
    basemapMode: 'none',
    basemapBufferPct: 0.5,
    showNorthArrow: false,
    planRotationDeg: 0,
    northAngleDeg: 0,
    address: '',
    parcelStreetLine: '',
  };
}

const initialPlannerState = mergePlannerInitialState(readPlannerSession(), initialDevPlanner);

/** ArcGIS / GeoJSON: address field name casing varies (`address`, `ADDRESS`, …). */
function readParcelAddressFromFeatureProps(rawProps) {
  if (!rawProps || typeof rawProps !== 'object') return '';
  const keys = Object.keys(rawProps);
  for (let i = 0; i < keys.length; i += 1) {
    if (String(keys[i]).toLowerCase() === 'address') {
      const v = rawProps[keys[i]];
      if (v != null && String(v).trim()) return String(v).trim();
    }
  }
  return '';
}

function normalizeSitePlanStreetFragment(raw) {
  let s = String(raw || '').trim();
  if (!s) return '';
  // Same idea as setup normalizeUserInput: 1425HELEN → 1425 HELEN (parcel DB often omits the space)
  s = s.replace(/^(\d+)([A-Za-z])/, '$1 $2');
  s = s.replace(/\./g, '');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
}

/**
 * Street number + street name for the plan title (no city/state/ZIP). Prefers parcel address when loaded.
 * Returns null for dimensions-only flows with no usable street line.
 */
function sitePlanStreetLineOnly(addressInput, parcelData, fallbackParcelAddress = '') {
  const features = parcelData?.features;
  const first = Array.isArray(features) && features[0] ? features[0] : null;
  const props = first?.properties || first?.attributes || {};
  let parcelAddr = readParcelAddressFromFeatureProps(props);
  if (!parcelAddr && fallbackParcelAddress) {
    parcelAddr = String(fallbackParcelAddress).trim();
  }

  let line = '';
  if (parcelAddr) {
    line = parcelAddr.split(',')[0].trim();
  } else if (addressInput != null && String(addressInput).trim()) {
    line = String(addressInput).split(',')[0].trim();
  } else {
    return null;
  }

  line = line.replace(/\s+\b\d{5}(-\d{4})?\s*$/i, '').trim();
  line = normalizeSitePlanStreetFragment(line);
  if (!line) return null;

  const parts = line.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  // First token = house number (e.g. 1234, 12-14, 100A)
  if (!/^\d+([/-]\d+)?[A-Za-z]?$/i.test(parts[0])) return null;

  return line.replace(/\s+/g, ' ');
}

function groupFilteredModuleTypes(filtered) {
  const byId = new Map(MODULE_CATEGORIES.map((c) => [c.id, []]));
  const other = [];
  filtered.forEach((m) => {
    const cid = m.categoryId;
    if (cid && byId.has(cid)) byId.get(cid).push(m);
    else other.push(m);
  });
  const sections = MODULE_CATEGORIES.map((c) => ({
    id: c.id,
    label: c.label,
    modules: byId.get(c.id) || [],
  })).filter((s) => s.modules.length > 0);
  if (other.length > 0) {
    sections.push({ id: 'other', label: 'Other', modules: other });
  }
  return sections;
}

/** Wildflower / tallgrass: Habitat list when that goal is on; else Stormwater-only sessions see them under Stormwater (not under both). */
const MEADOW_PRAIRIE_PICKER_IDS = new Set(['native-meadow', 'tallgrass-prairie']);

function groupFilteredModulesByGoals(filtered, selectedGoalIds) {
  // If nothing is selected, fall back to the original category grouping.
  if (!selectedGoalIds || selectedGoalIds.length === 0) return groupFilteredModuleTypes(filtered);

  const byGoalId = new Map(PROJECT_GOALS.map((g) => [g.id, []]));

  const hasHabitat = selectedGoalIds.includes('habitat-beautification');
  const hasStorm = selectedGoalIds.includes('stormwater-management');

  filtered.forEach((m) => {
    const pGoals = m.projectGoals || [];

    if (MEADOW_PRAIRIE_PICKER_IDS.has(m.id)) {
      if (hasHabitat && pGoals.includes('habitat-beautification')) {
        byGoalId.get('habitat-beautification').push(m);
      } else if (!hasHabitat && hasStorm && pGoals.includes('stormwater-management')) {
        byGoalId.get('stormwater-management').push(m);
      }
      PROJECT_GOALS.forEach((g) => {
        if (g.id === 'habitat-beautification' || g.id === 'stormwater-management') return;
        if (selectedGoalIds.includes(g.id) && pGoals.includes(g.id)) {
          byGoalId.get(g.id).push(m);
        }
      });
      return;
    }

    // When stormwater is selected, show rain gardens only under stormwater-management
    // (even though the module's own projectGoals include habitat-beautification).
    if (m.id === 'rain-garden' && hasStorm) {
      byGoalId.get('stormwater-management').push(m);
      return;
    }

    // Shade tree is conditional: show in Shared Spaces only when Habitat is NOT selected.
    if (m.id === 'shade-tree') {
      if (hasHabitat && pGoals.includes('habitat-beautification')) {
        byGoalId.get('habitat-beautification').push(m);
      } else if (!hasHabitat && pGoals.includes('shared-spaces')) {
        byGoalId.get('shared-spaces').push(m);
      }
      return;
    }

    PROJECT_GOALS.forEach((g) => {
      if (selectedGoalIds.includes(g.id) && pGoals.includes(g.id)) {
        byGoalId.get(g.id).push(m);
      }
    });
  });

  const sections = PROJECT_GOALS.map((g) => ({
    id: g.id,
    label: g.label,
    modules: byGoalId.get(g.id) || [],
  })).filter((s) => selectedGoalIds.includes(s.id) && s.modules.length > 0);

  return sections;
}

/** Base sidebar swatch colors per project goal (paths & clean energy use a single flat color). */
const GOAL_SWATCH_BASE_HEX = {
  'food-production': '#EFB822',
  'habitat-beautification': '#8AAA50',
  'stormwater-management': '#599AA0',
  'shared-spaces': '#F68A4C',
  'paths-edges': '#EF6B42',
  'clean-energy': '#E1F1E1',
  'waste-recycling': '#8AAA50',
};

/** When no goals are selected, category sections use the closest goal palette for swatches. */
const MODULE_CATEGORY_TO_PALETTE_GOAL = {
  'rain-water': 'stormwater-management',
  stormwater: 'stormwater-management',
  vegetation: 'habitat-beautification',
  'roofs-energy': 'clean-energy',
};

function clamp255(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function mixHexWithWhite(hex, t) {
  const h = String(hex).replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return hex;
  const u = Math.max(0, Math.min(1, t));
  const rr = r + (255 - r) * u;
  const gg = g + (255 - g) * u;
  const bb = b + (255 - b) * u;
  return `#${clamp255(rr).toString(16).padStart(2, '0')}${clamp255(gg).toString(16).padStart(2, '0')}${clamp255(bb).toString(16).padStart(2, '0')}`;
}

/** Picker swatch: lighter steps through the section list; paths & clean energy stay one color. */
function goalPickerSwatchColor(goalId, indexInSection, sectionCount) {
  const base = GOAL_SWATCH_BASE_HEX[goalId] || '#6b7280';
  if (goalId === 'paths-edges' || goalId === 'clean-energy') {
    return base;
  }
  const n = Math.max(1, sectionCount);
  const maxLift = 0.52;
  const t = n <= 1 ? 0 : (indexInSection / (n - 1)) * maxLift;
  return mixHexWithWhite(base, t);
}

function pickerPaletteGoalIdForSection(sectionId) {
  if (GOAL_SWATCH_BASE_HEX[sectionId]) return sectionId;
  return MODULE_CATEGORY_TO_PALETTE_GOAL[sectionId] || 'stormwater-management';
}

/** 5 ft. inset on each side as % of plan (matches PLAN_PX_PER_FOOT × zoom visually). */
const PlanSetbackRect = ({ siteWidth, siteLength }) => {
  const wFtPlan = Math.max(1, Number(siteWidth) || 1);
  const lFtPlan = Math.max(1, Number(siteLength) || 1);
  const insetPctW = Math.min((SETBACK_FEET / wFtPlan) * 100, 49);
  const insetPctL = Math.min((SETBACK_FEET / lFtPlan) * 100, 49);
  return (
    <rect
      x={`${insetPctW}%`}
      y={`${insetPctL}%`}
      width={`${100 - 2 * insetPctW}%`}
      height={`${100 - 2 * insetPctL}%`}
      fill="none"
      stroke={SETBACK_LINE_COLOR}
      strokeWidth="2"
      strokeDasharray={SETBACK_DASH}
    />
  );
};

const LEGEND_LINE_W = 56;
const LEGEND_LINE_H = 14;

/** Sits to the left of the plan (10px gap); tops align with property (lot) box via flex items-start */
const PlanLegend = () => (
  <aside
    className="pointer-events-none shrink-0 max-w-[11rem] text-left"
    aria-label="Site plan legend"
  >
    <h3 className="mb-3 mt-0 text-xl font-semibold text-gray-800">LEGEND</h3>
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <svg
          width={LEGEND_LINE_W}
          height={LEGEND_LINE_H}
          className="shrink-0 overflow-visible"
          aria-hidden
        >
          <line
            x1="2"
            y1={LEGEND_LINE_H / 2}
            x2={LEGEND_LINE_W - 2}
            y2={LEGEND_LINE_H / 2}
            stroke="#1f2937"
            strokeWidth="2"
            strokeDasharray={PLAN_BOUNDARY_DASH.join(' ')}
          />
        </svg>
        <span className="text-sm text-gray-700 leading-tight">Property Boundaries</span>
      </div>
      <div className="flex items-center gap-2.5">
        <svg
          width={LEGEND_LINE_W}
          height={LEGEND_LINE_H}
          className="shrink-0 overflow-visible"
          aria-hidden
        >
          <line
            x1="2"
            y1={LEGEND_LINE_H / 2}
            x2={LEGEND_LINE_W - 2}
            y2={LEGEND_LINE_H / 2}
            stroke={SETBACK_LINE_COLOR}
            strokeWidth="2"
            strokeDasharray={SETBACK_DASH}
          />
        </svg>
        <span className="text-sm text-gray-700 leading-tight">5 ft Setback</span>
      </div>
    </div>
  </aside>
);

/** Below LEGEND, left of plan; matches PlanLegend typography and layout */
const PlanModuleToolTips = () => (
  <aside
    className="pointer-events-none shrink-0 max-w-[11rem] text-left"
    aria-label="Module tool tips"
  >
    <h3 className="mb-3 mt-0 text-xl font-semibold text-gray-800">MODULE TOOL TIPS</h3>
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-0">
        <span
          className="shrink-0 -ml-[3px] w-[10px] pt-0.5 text-left text-sm text-gray-700 leading-none"
          aria-hidden
        >
          •
        </span>
        <span className="min-w-0 pl-[2px] text-sm text-gray-700 leading-tight">Drag center to move</span>
      </div>
      <div className="flex items-start gap-0">
        <span
          className="shrink-0 -ml-[3px] w-[10px] pt-0.5 text-left text-sm text-gray-700 leading-none"
          aria-hidden
        >
          •
        </span>
        <span className="min-w-0 pl-[2px] text-sm text-gray-700 leading-tight">Drag blue handles to resize</span>
      </div>
      <div className="flex items-start gap-0">
        <span
          className="shrink-0 -ml-[3px] w-[10px] pt-0.5 text-left text-sm text-gray-700 leading-none"
          aria-hidden
        >
          •
        </span>
        <span className="min-w-0 pl-[2px] text-sm text-gray-700 leading-tight">Drag green button to rotate freely</span>
      </div>
      <div className="flex items-start gap-0">
        <span
          className="shrink-0 -ml-[3px] w-[10px] pt-0.5 text-left text-sm text-gray-700 leading-none"
          aria-hidden
        >
          •
        </span>
        <span className="min-w-0 pl-[2px] text-sm text-gray-700 leading-tight">Red trash can deletes</span>
      </div>
    </div>
  </aside>
);

const LIDSitePlanner = () => {
  const [siteWidth, setSiteWidth] = useState(initialPlannerState.siteWidth);
  const [siteLength, setSiteLength] = useState(initialPlannerState.siteLength);
  const [siteCreated, setSiteCreated] = useState(initialPlannerState.siteCreated);
  const [modules, setModules] = useState(initialPlannerState.modules);
  const [selectedModule, setSelectedModule] = useState(null);
  const [draggedModule, setDraggedModule] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [rotateHandle, setRotateHandle] = useState(null);
  const dragRafRef = useRef(null);
  const pendingDragRef = useRef(null);
  const minSizeWarnedRef = useRef(false);
  // Basemap preview mode: 'none' | 'leaflet' | 'static'
  const [basemapMode, setBasemapMode] = useState(initialPlannerState.basemapMode);
  // Basemap buffer around parcel bounds (fractional, e.g., 0.25 = 25%)
  const [basemapBufferPct, setBasemapBufferPct] = useState(initialPlannerState.basemapBufferPct);
  const leafletMapRef = useRef(null);
  const leafletDivRef = useRef(null);
  // Trigger parcel redraw on container resize
  const [parcelRenderTick, setParcelRenderTick] = useState(0);
  const [showNorthArrow, setShowNorthArrow] = useState(initialPlannerState.showNorthArrow);
  const [planRotationDeg, setPlanRotationDeg] = useState(initialPlannerState.planRotationDeg);
  const planOuterRef = useRef(null);
  const planInnerRef = useRef(null);
  // North arrow rotation (degrees)
  const [northAngleDeg, setNorthAngleDeg] = useState(initialPlannerState.northAngleDeg);
  const [northRotation, setNorthRotation] = useState(initialPlannerState.northRotation);
  /** Used for PDF capture via html2canvas (captures full plan contents). */
  const canvasRef = useRef(null);
  /** Parcel/bounds overlay canvas (drawn via 2D ctx). */
  const parcelCanvasRef = useRef(null);

  // Site filtering states
  const [address, setAddress] = useState(initialPlannerState.address ?? '');
  const [addressWarning, setAddressWarning] = useState('');
  /** Parcel `address` field for plan title; kept after refresh via session. */
  const [parcelAddressSnapshot, setParcelAddressSnapshot] = useState(
    initialPlannerState.parcelStreetLine ?? ''
  );
  const [siteType, setSiteType] = useState(initialPlannerState.siteType);
  const [soilType, setSoilType] = useState(initialPlannerState.soilType);
  const [projectGoals, setProjectGoals] = useState(initialPlannerState.projectGoals);
  const [siteTypeOptions, setSiteTypeOptions] = useState([]);
  const [hasRoof, setHasRoof] = useState(initialPlannerState.hasRoof);
  const [hasUtilities, setHasUtilities] = useState(initialPlannerState.hasUtilities);
  const [isLookingUpAddress, setIsLookingUpAddress] = useState(false);

  // Roof-driven rain garden sizing inputs and computed minimum
  const [roofLengthFt, setRoofLengthFt] = useState(initialPlannerState.roofLengthFt);
  const [roofWidthFt, setRoofWidthFt] = useState(initialPlannerState.roofWidthFt);
  const [roofAreaSqFt, setRoofAreaSqFt] = useState(initialPlannerState.roofAreaSqFt);
  const [targetRainfallIn, setTargetRainfallIn] = useState(initialPlannerState.targetRainfallIn);
  const [pondingDepthIn, setPondingDepthIn] = useState(initialPlannerState.pondingDepthIn);
  const [minRainGardenAreaSqFt, setMinRainGardenAreaSqFt] = useState(initialPlannerState.minRainGardenAreaSqFt);

  // Topography data states
  const [elevationData, setElevationData] = useState(null);
  const [contourLines, setContourLines] = useState([]);
  const [isLoadingTopography, setIsLoadingTopography] = useState(false);

  // Existing structures (roof area)
  const [existingStructures, setExistingStructures] = useState(initialPlannerState.existingStructures);

  // Parcel map states
  const [showParcelMap, setShowParcelMap] = useState(false);
  const [parcelData, setParcelData] = useState(null);
  const [isLoadingParcelData, setIsLoadingParcelData] = useState(false);
  const [parcelBounds, setParcelBounds] = useState(null);
  const [buildingFootprints, setBuildingFootprints] = useState(null);
  const [isLoadingBuildings, setIsLoadingBuildings] = useState(false);
  // Temporarily disabled globally until footprint geometry issues are resolved.
  const [showBuildingFootprints, setShowBuildingFootprints] = useState(false);
  /** View zoom only: multiplies on-screen px/ft; module math stays in feet & %. */
  const [planViewZoom, setPlanViewZoom] = useState(initialPlannerState.planViewZoom);
  /** Quantity for modules that allow multiple fixed-size placements */
  const [moduleQuantities, setModuleQuantities] = useState(initialPlannerState.moduleQuantities);
  /** Module picker qty editing: draft values so Add doesn’t flicker / fight blur timing. */
  const [pickerQtyEditing, setPickerQtyEditing] = useState({});
  const [pickerQtyDraft, setPickerQtyDraft] = useState({});

  // Keep the displayed quantity inputs in sync with what is already on the plan
  // so clicking an “already added” row doesn’t unexpectedly reset the draft to 0.
  useEffect(() => {
    const qtyTypeIds = [
      'rain-barrel',
      'water-tank',
      'fruit-tree',
      'shade-tree',
      'raised-bed',
      'garden-row',
      'hoop-house',
    ];
    setModuleQuantities((prev) => {
      const next = { ...prev };
      let changed = false;
      qtyTypeIds.forEach((id) => {
        const count = modules.filter((m) => m.type === id).length;
        const prevVal = next[id] ?? 0;
        if (prevVal !== count) {
          next[id] = count;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [modules]);

  /** PDF paper size choice */
  const [pdfPaperSize, setPdfPaperSize] = useState('letter'); // 'letter' | 'tabloid'

  /** Rain garden plugs: 1 per sq ft (no slider). Bioswale grass plugs use 3× this spacing. */
  const plugSpacing = 1;

  useEffect(() => {
    const f = parcelData?.features?.[0];
    const p = f?.properties || f?.attributes;
    const a = readParcelAddressFromFeatureProps(p);
    if (a) setParcelAddressSnapshot(a);
  }, [parcelData]);

  // Site plan box: base px/ft × view zoom. Scroll plan area when larger than panel.
  const getPlanBoxStyle = useCallback(() => {
    const wFt = Math.max(1, Number(siteWidth) || 1);
    const lFt = Math.max(1, Number(siteLength) || 1);
    const z = clampPlanZoom(planViewZoom);
    const widthPx = Math.round(wFt * PLAN_PX_PER_FOOT * z);
    const heightPx = Math.round(lFt * PLAN_PX_PER_FOOT * z);
    return {
      width: `${widthPx}px`,
      height: `${heightPx}px`,
      backgroundColor: 'transparent',
    };
  }, [siteWidth, siteLength, planViewZoom]);

  // Load distinct site type options from ArcGIS parcels API on startup
  useEffect(() => {
    let isCancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          where: "property_class_description IS NOT NULL",
          outFields: "property_class_description",
          returnDistinctValues: "true",
          f: "json"
        });
        const url = `https://services2.arcgis.com/qvkbeam7Wirps6zC/arcgis/rest/services/parcel_file_current/FeatureServer/0/query?${params.toString()}`;
        const res = await fetch(url);
        if (!res.ok) return;
        const json = await res.json();
        const features = Array.isArray(json.features) ? json.features : [];
        const values = Array.from(new Set(features
          .map(f => (f.attributes || f.properties || {}).property_class_description)
          .filter(Boolean)));
        values.sort((a, b) => String(a).localeCompare(String(b)));
        if (!isCancelled) setSiteTypeOptions(values);
      } catch (_) {
        // ignore; options remain empty
      }
    })();
    return () => { isCancelled = true; };
  }, []);
  
  // Debug states for persistent display
  const [debugMessages, setDebugMessages] = useState([]);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(32); // in rem units
  const [isDragging, setIsDragging] = useState(false);
  const dividerDragRef = useRef({ active: false, startClientX: 0, startWidthRem: 32 });
  const sidebarScrollRef = useRef(null);

  const handleDividerMouseMove = useCallback((e) => {
    if (!dividerDragRef.current.active) return;
    const { startClientX, startWidthRem } = dividerDragRef.current;
    const deltaX = e.clientX - startClientX;
    const deltaRem = deltaX / 16;
    const newWidth = startWidthRem + deltaRem;
    const minWidthRem = 600 / 16;
    const maxWidthRem = 900 / 16;
    if (newWidth >= minWidthRem && newWidth <= maxWidthRem) {
      setSidebarWidth(newWidth);
    }
  }, []);

  const handleDividerMouseUp = useCallback(() => {
    dividerDragRef.current.active = false;
    setIsDragging(false);
  }, []);

  const handleDividerMouseDown = (e) => {
    dividerDragRef.current = {
      active: true,
      startClientX: e.clientX,
      startWidthRem: sidebarWidth,
    };
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return undefined;
    document.addEventListener('mousemove', handleDividerMouseMove);
    document.addEventListener('mouseup', handleDividerMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handleDividerMouseMove);
      document.removeEventListener('mouseup', handleDividerMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleDividerMouseMove, handleDividerMouseUp]);

  // Same sidebar scroll container is used for setup and planner; reset scroll when switching so the planner does not
  // open scrolled to where the Launch button was.
  useEffect(() => {
    const el = sidebarScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [siteCreated]);

  // While the planner is open, remember this tab’s state so a normal refresh keeps you here (cleared on BACK).
  useEffect(() => {
    if (!siteCreated) return;
    try {
      sessionStorage.setItem(
        PLANNER_SESSION_KEY,
        JSON.stringify({
          v: PLANNER_SESSION_VERSION,
          siteCreated: true,
          siteWidth,
          siteLength,
          address,
          parcelStreetLine: parcelAddressSnapshot,
          projectGoals,
          soilType,
          siteType,
          hasRoof,
          hasUtilities,
          roofLengthFt,
          roofWidthFt,
          roofAreaSqFt,
          targetRainfallIn,
          pondingDepthIn,
          minRainGardenAreaSqFt,
          modules,
          existingStructures,
          moduleQuantities,
          northRotation,
          planViewZoom,
          basemapMode,
          basemapBufferPct,
          showNorthArrow,
          planRotationDeg,
          northAngleDeg,
        })
      );
    } catch {
      /* ignore quota or private mode */
    }
  }, [
    siteCreated,
    siteWidth,
    siteLength,
    address,
    parcelAddressSnapshot,
    projectGoals,
    soilType,
    siteType,
    hasRoof,
    hasUtilities,
    roofLengthFt,
    roofWidthFt,
    roofAreaSqFt,
    targetRainfallIn,
    pondingDepthIn,
    minRainGardenAreaSqFt,
    modules,
    existingStructures,
    moduleQuantities,
    northRotation,
    planViewZoom,
    basemapMode,
    basemapBufferPct,
    showNorthArrow,
    planRotationDeg,
    northAngleDeg,
  ]);

  // Add debug message function
  const addDebugMessage = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugMessages(prev => [...prev.slice(-19), `${timestamp}: ${message}`]); // Keep more messages
    console.log(message); // Also log to console
  };

  // Add initial debug message
  useEffect(() => {
    addDebugMessage('LID Site Planner loaded — use Show Debug Panel to open the log');
  }, []);

  // Monitor site dimensions changes
  useEffect(() => {
    addDebugMessage(`Site dimensions changed: Width=${siteWidth}, Length=${siteLength}`);
  }, [siteWidth, siteLength]);

  const moduleTypes = [
    { 
      id: 'roof-area', 
      name: 'Roof Area', 
      color: '#6b7280', 
      widthFt: 30, 
      heightFt: 20, 
      benefits: 'Runoff source for LID modules',
      depthIn: 0,
      soilMixDepthIn: 0,
      reference: 'Site Input',
      // Filtering properties
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed', 'urban-disturbed', 'organic'],
      projectGoals: ['stormwater-management'],
      requiresRoof: false,
      requiresUtilities: false,
      isRoofArea: true // Special flag for roof area module
    },
    { 
      id: 'rain-garden', 
      name: 'Rain Garden', 
      color: '#10b981', 
      widthFt: 20, 
      heightFt: 15, 
      benefits: 'Stormwater: 500-1000 gal/storm',
      depthIn: 6,
      soilMixDepthIn: 30,
      reference: 'DWSD Ch. 8',
      // Filtering properties
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['stormwater-management', 'habitat-beautification'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'rain-water',
    },
    {
      id: 'rain-barrel',
      name: 'Rain Barrel',
      color: '#0284c7',
      widthFt: 5,
      heightFt: 5,
      fixedFootprint: true,
      quantitySelector: true,
      benefits: 'Captures roof runoff for reuse',
      depthIn: 0,
      soilMixDepthIn: 0,
      reference: 'Residential rainwater harvesting',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['any'],
      projectGoals: ['stormwater-management'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'rain-water',
    },
    {
      id: 'water-tank',
      name: 'Water Tank (250 gal+)',
      color: '#0369a1',
      widthFt: 5,
      heightFt: 5,
      fixedFootprint: true,
      quantitySelector: true,
      benefits: 'Stores 250+ gal on site',
      depthIn: 0,
      soilMixDepthIn: 0,
      reference: 'DWSD Ch. 11',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['any'],
      projectGoals: ['stormwater-management'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'rain-water',
    },
    { 
      id: 'bioswale', 
      name: 'Bioswale', 
      color: '#86efac', 
      widthFt: 10, 
      heightFt: 10, 
      benefits: 'Filters runoff, 300 gal/storm',
      depthIn: 12,
      soilMixDepthIn: 18,
      reference: 'DWSD Ch. 8',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['stormwater-management'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'stormwater',
    },
    { 
      id: 'permeable-paving', 
      name: 'Permeable Pavement', 
      color: '#94a3b8', 
      widthFt: 10, 
      heightFt: 10, 
      benefits: 'Reduces runoff 80%',
      depthIn: 4,
      baseDepthIn: 12,
      reference: 'DWSD Ch. 10',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['sandy', 'loamy', 'mixed'],
      projectGoals: ['stormwater-management', 'paths-edges', 'shared-spaces'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'stormwater',
    },
    {
      id: 'green-roof',
      name: 'Green Roof Area',
      color: '#22c55e',
      widthFt: 10,
      heightFt: 10,
      benefits: 'Insulation, air quality',
      depthIn: 4,
      reference: 'DWSD Ch. 12',
      siteTypes: ['commercial', 'institutional', 'residential'],
      soilTypes: ['any'],
      projectGoals: ['stormwater-management', 'clean-energy', 'habitat-beautification'],
      requiresRoof: true,
      requiresUtilities: false,
      categoryId: 'roofs-energy',
      inactive: true, // hidden from module picker; keep type for existing plans / calculators
    },
    {
      id: 'tree-planting',
      name: 'Tree Grove',
      color: '#059669',
      widthFt: 10,
      heightFt: 10,
      benefits: 'CO₂: 48 lbs/yr/tree',
      reference: 'DWSD Ch. 15',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['habitat-beautification'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'vegetation',
    },
    {
      id: 'fruit-tree',
      name: 'Fruit Tree',
      color: '#15803d',
      widthFt: 20,
      heightFt: 20,
      fixedFootprint: true,
      quantitySelector: true,
      benefits: 'CO₂: 48 lbs/yr/tree (fruit tree)',
      reference: 'DWSD Ch. 15',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['food-production'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'vegetation',
    },
    {
      id: 'raised-bed',
      name: 'Raised Bed',
      color: '#92400e',
      widthFt: 5,
      heightFt: 10,
      fixedFootprint: true,
      quantitySelector: true,
      benefits: 'Vegetable / herb growing space',
      depthIn: 12,
      soilMixDepthIn: 12,
      reference: 'Residential food production',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['food-production'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'vegetation',
    },
    {
      id: 'garden-row',
      name: 'Garden Row',
      color: '#3f6212',
      widthFt: 5,
      heightFt: 20,
      fixedFootprint: true,
      quantitySelector: true,
      benefits: 'In-ground row crop / market garden',
      depthIn: 8,
      soilMixDepthIn: 8,
      reference: 'Residential food production',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['food-production'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'vegetation',
    },
    {
      id: 'hoop-house',
      name: 'Hoop House',
      color: '#0f766e',
      widthFt: 20,
      heightFt: 60,
      fixedFootprint: true,
      quantitySelector: true,
      benefits: 'Extended-season protected growing (20 ft × 60 ft)',
      depthIn: 0,
      soilMixDepthIn: 0,
      reference: 'High tunnel / hoop house (residential & small farm)',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['food-production'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'vegetation',
    },
    {
      id: 'shade-tree',
      name: 'Shade Tree',
      color: '#047857',
      widthFt: 30,
      heightFt: 30,
      fixedFootprint: true,
      quantitySelector: true,
      benefits: 'CO₂: 48 lbs/yr/tree (shade)',
      reference: 'DWSD Ch. 15',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['habitat-beautification', 'shared-spaces'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'vegetation',
    },
    {
      id: 'bench',
      name: 'Bench',
      color: '#6b7280',
      widthFt: 5,
      heightFt: 10,
      fixedFootprint: true,
      quantitySelector: true,
      benefits: 'Seating for shared spaces',
      depthIn: 0,
      soilMixDepthIn: 0,
      reference: 'Planning estimate',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['any'],
      projectGoals: ['shared-spaces'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'vegetation',
    },
    { 
      id: 'native-meadow', 
      name: 'Wildflower Meadow', 
      color: '#fbbf24', 
      widthFt: 10, 
      heightFt: 20, 
      benefits: 'Pollinators; carbon storage & water infiltration (resize on plan)',
      reference: 'Michigan native',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['habitat-beautification', 'stormwater-management'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'vegetation',
    },
    { 
      id: 'tallgrass-prairie', 
      name: 'Tallgrass Prairie', 
      color: '#d97706', 
      widthFt: 10, 
      heightFt: 20, 
      benefits: 'Carbon storage, deep roots & high infiltration (resize on plan)',
      reference: 'Native restoration',
      siteTypes: ['residential', 'commercial', 'institutional', 'public'],
      soilTypes: ['clay', 'sandy', 'loamy', 'mixed'],
      projectGoals: ['habitat-beautification', 'stormwater-management'],
      requiresRoof: false,
      requiresUtilities: false,
      categoryId: 'vegetation',
    },
    { 
      id: 'cistern', 
      name: 'Rainwater Cistern', 
      color: '#3b82f6', 
      widthFt: 10, 
      heightFt: 10, 
      benefits: 'Stores 1000+ gal',
      reference: 'DWSD Ch. 11',
      siteTypes: ['residential', 'commercial', 'institutional'],
      soilTypes: ['any'],
      projectGoals: ['stormwater-management'],
      requiresRoof: true,
      requiresUtilities: true,
      categoryId: 'roofs-energy',
    },
    { 
      id: 'solar-array', 
      name: 'Solar Array', 
      color: '#7c3aed', 
      widthFt: 10, 
      heightFt: 10, 
      benefits: 'Clean energy',
      reference: 'Sustainability',
      siteTypes: ['residential', 'commercial', 'institutional'],
      soilTypes: ['any'],
      projectGoals: ['clean-energy'],
      requiresRoof: false,
      requiresUtilities: true,
      categoryId: 'roofs-energy',
    },
  ];

  const createSite = () => {
    const width = Number(siteWidth) || 0;
    const length = Number(siteLength) || 0;
    
    if (width > 0 && length > 0) {
      // Default start zoom: skinny lots (<=30 ft frontage) open at 200% for easier module placement.
      setPlanViewZoom(width <= 30 ? 2 : 1);
      // If roof area provided, require soil type to size rain garden
      const roofArea = Number(roofAreaSqFt);
      if (!soilType && !isNaN(roofArea) && roofArea > 0) {
        alert('Please select a Soil Type to size the rain garden.');
        return;
      }

      setSiteCreated(true);
      // Remove any duplicate rain gardens, keep the first if present
      setModules(prev => {
        let seen = false;
        return prev.filter(m => {
          if (m.type !== 'rain-garden') return true;
          if (!seen) { seen = true; return true; }
          return false;
        });
      });
      
      // Topography contours disabled; skip mock generation
      
      // If roof area provided, compute minimum rain garden and add or size it
      addDebugMessage(`createSite: roofArea=${roofArea}, roofAreaSqFt=${roofAreaSqFt}`);
      if (!isNaN(roofArea) && roofArea > 0) {
        const minArea = computeMinRainGardenArea(roofArea, targetRainfallIn, pondingDepthIn, soilType);
        addDebugMessage(`createSite: computed minArea=${minArea} for roofArea=${roofArea}`);
        setMinRainGardenAreaSqFt(minArea);
        addDebugMessage(`createSite: calling ensureRainGardenMinSize with minArea=${minArea}`);
        ensureRainGardenMinSize(
          minArea,
          goalPickerSwatchColor(
            projectGoals.includes('stormwater-management')
              ? 'stormwater-management'
              : 'habitat-beautification',
            0,
            1
          )
        );
      } else {
        addDebugMessage(`createSite: no roof area provided, setting minRainGardenAreaSqFt to null`);
        setMinRainGardenAreaSqFt(null);
      }
    }
  };

  const startOver = () => {
    // Keep user on the same "site planner" page; clear placed modules so they can reselect.
    setModules([]);
    setSelectedModule(null);
    setDraggedModule(null);
    setResizeHandle(null);
    setNorthRotation(0);

    // Preserve roof visuals (derived from roof dimensions), but clear the rest of the canvas placements.
    const roofLength = parseFloat(roofLengthFt) || 0;
    const roofWidth = parseFloat(roofWidthFt) || 0;
    const roofArea = roofLength * roofWidth;

    if (roofArea > 0) {
      setExistingStructures([
        {
          id: 'roof-area',
          name: 'Roof Area',
          xPercent: 10,
          yPercent: 10,
          widthFt: roofWidth,
          heightFt: roofLength,
          rotation: 0,
          color: '#6b7280',
        },
      ]);
    } else {
      setExistingStructures([]);
    }
  };

  // Normalize parcel property_class_description to canonical site type categories
  const canonicalizeSiteType = useCallback((raw) => {
    if (!raw) return '';
    const v = String(raw).toLowerCase();
    if (v.includes('residential')) return 'residential';
    if (v.includes('commercial') || v.includes('industrial')) return 'commercial';
    if (v.includes('institutional')) return 'institutional';
    if (v.includes('public')) return 'public';
    return '';
  }, []);

  // Filter modules based primarily on project goals (ignore parcel site type)
  const getFilteredModules = () => {
    return moduleTypes.filter(module => {
      // Exclude roof area module from regular module list (it's auto-created)
      if (module.isRoofArea) return false;

      if (module.inactive) return false;

      // With roof runoff + soil, rain garden is sized to minimum sq ft (shown under Stormwater), not the 20×15 catalog tile
      if (module.id === 'rain-garden' && roofAreaSqFt > 0 && soilType) return false;

      // Do NOT filter by parcel site type; goals drive availability
      
      // Check soil type (map new soil types to old ones for compatibility)
      if (soilType && !module.soilTypes.includes('any')) {
        const mappedSoilType = mapNewSoilTypeToOld(soilType);
        if (!module.soilTypes.includes(mappedSoilType)) return false;
      }
      
      // Check project goals (at least one goal must match)
      if (projectGoals.length > 0) {
        const hasMatchingGoal = projectGoals.some(goal => module.projectGoals.includes(goal));
        if (!hasMatchingGoal) return false;
      }
      
      // Check roof requirement
      if (module.requiresRoof && !hasRoof) return false;
      
      // Utilities is no longer a limiting factor for module availability
      
      return true;
    });
  };

  /** Matches Add Modules sidebar swatches so plan blocks, module list, and PDF use the same color. */
  const getPlanSwatchForModuleType = (moduleTypeId) => {
    const filtered = getFilteredModules();
    const sections = groupFilteredModulesByGoals(filtered, projectGoals);
    const roofRgMinArea =
      roofAreaSqFt > 0 && soilType
        ? minRainGardenAreaSqFt != null && minRainGardenAreaSqFt > 0
          ? minRainGardenAreaSqFt
          : computeMinRainGardenArea(roofAreaSqFt, targetRainfallIn, pondingDepthIn, soilType)
        : null;
    const showRoofRainGardenRow = roofRgMinArea != null && roofRgMinArea > 0;
    const roofRgSectionId = projectGoals.includes('stormwater-management')
      ? 'stormwater-management'
      : projectGoals.includes('habitat-beautification')
        ? 'habitat-beautification'
        : null;
    const roofRgSectionInList =
      roofRgSectionId != null && sections.some((s) => s.id === roofRgSectionId);

    if (moduleTypeId === 'rain-garden' && showRoofRainGardenRow && !roofRgSectionInList) {
      return goalPickerSwatchColor('stormwater-management', 0, 1);
    }

    for (let si = 0; si < sections.length; si++) {
      const section = sections[si];
      const types = section.modules;
      const paletteGoal = pickerPaletteGoalIdForSection(section.id);
      const showStripeHere = showRoofRainGardenRow && roofRgSectionId === section.id;
      const sectionModuleCount = Math.max(1, types.length + (showStripeHere ? 1 : 0));

      if (showStripeHere && moduleTypeId === 'rain-garden' && !types.some((t) => t.id === 'rain-garden')) {
        return goalPickerSwatchColor(paletteGoal, 0, sectionModuleCount);
      }

      const idx = types.findIndex((t) => t.id === moduleTypeId);
      if (idx >= 0) {
        const swatchIdx = showStripeHere ? idx + 1 : idx;
        return goalPickerSwatchColor(paletteGoal, swatchIdx, sectionModuleCount);
      }
    }

    const mt = moduleTypes.find((m) => m.id === moduleTypeId);
    return (mt && mt.color) || '#6b7280';
  };

  /** Plan / PDF fill: use color from Add click when it differs from catalog; else sync to current listing. */
  const getPlanBlockFillColor = (module) => {
    if (module.isRoofArea) return '#f3f4f6';
    const catalogHex = moduleTypes.find((mt) => mt.id === module.type)?.color;
    const stored = module.color;
    if (
      stored &&
      catalogHex &&
      String(stored).toLowerCase() !== String(catalogHex).toLowerCase()
    ) {
      return stored;
    }
    return getPlanSwatchForModuleType(module.type);
  };

  // Helper function to toggle project goals
  const toggleProjectGoal = (goal) => {
    setProjectGoals(prev => 
      prev.includes(goal) 
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  // Address lookup function (simplified for now)
  const lookupAddress = async () => {
    console.log('LOOKUP FUNCTION CALLED - This should appear in browser console');
    addDebugMessage('LOOKUP FUNCTION CALLED - This should appear in debug panel');
    
    if (!address.trim()) {
      setAddressWarning('Zip code is required');
      addDebugMessage('ERROR: No address provided');
      return;
    }

    // Normalize common input issues without changing what the user sees
    const normalizeUserInput = (addr) => {
      if (!addr) return '';
      let a = addr;
      // Insert a space between street number and letters, e.g., 1425HELEN -> 1425 HELEN
      a = a.replace(/^(\d+)([A-Za-z])/, '$1 $2');
      // Remove periods
      a = a.replace(/\./g, '');
      // Collapse multiple spaces
      a = a.replace(/\s+/g, ' ').trim();
      return a;
    };

    const normalizedAddressInput = normalizeUserInput(address);
    if (normalizedAddressInput !== address) {
      addDebugMessage(`Auto-corrected address for lookup: "${normalizedAddressInput}"`);
    }

    // Require a 5-digit ZIP code in the (normalized) input before proceeding
    const zipMatch = normalizedAddressInput.match(/\b(\d{5})(-\d{4})?\b/);
    if (!zipMatch) {
      setAddressWarning('Zip code is required');
      addDebugMessage('No zip code found in address input; stopping lookup');
      return;
    }
    
    addDebugMessage(`Starting address lookup for: "${address}"`);
    addDebugMessage(`Clearing previous parcel data and dimensions`);
    
    // Clear previous parcel data and reset dimensions
    setParcelData(null);
    setParcelBounds(null);
    setBuildingFootprints(null);
    setPlanViewZoom(1);
    setSiteWidth(0);
    setSiteLength(0);
    
    setIsLookingUpAddress(true);
    
    // Clear previous warnings
    setAddressWarning('');
    try {
      // 1) Geocode using Nominatim (try multiple approaches)
      addDebugMessage('Step 1: Geocoding address...');
      
      let geocodeData = null;
      let geocodeError = null;
      
      // Try multiple geocoding approaches to handle CORS issues (prefer CORS-friendly first)
      const geocodingUrls = [
        {
          name: 'Maps.co (CORS friendly)',
          url: `https://geocode.maps.co/search?q=${encodeURIComponent(normalizedAddressInput)}`
        },
        {
          name: 'AllOrigins (Nominatim via proxy)',
          url: `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=5&q=${encodeURIComponent(normalizedAddressInput)}`)}`
        },
        {
          name: 'Direct Nominatim',
          url: `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=5&q=${encodeURIComponent(normalizedAddressInput)}`
        },
        {
          name: 'CORS Anywhere (may require prior enablement)',
          url: `https://cors-anywhere.herokuapp.com/https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=5&q=${encodeURIComponent(normalizedAddressInput)}`
        }
      ];

      for (const geocodingMethod of geocodingUrls) {
        try {
          addDebugMessage(`Trying ${geocodingMethod.name} geocoding...`);
          const response = await fetch(geocodingMethod.url);
          
          if (response.ok) {
            geocodeData = await response.json();
            addDebugMessage(`${geocodingMethod.name} geocoding successful: ${geocodeData.length} results`);
            break; // Success, exit the loop
          } else {
            addDebugMessage(`${geocodingMethod.name} failed with status: ${response.status}`);
          }
        } catch (e) {
          addDebugMessage(`${geocodingMethod.name} failed: ${e.message}`);
          geocodeError = e;
        }
      }
      
      // Normalize maps.co format to Nominatim-like array
      if (geocodeData && Array.isArray(geocodeData) && geocodeData.length > 0 && geocodeData[0] && geocodeData[0].display_name && geocodeData[0].lat && geocodeData[0].lon) {
        // OK as-is
      }

      if (!geocodeData || !Array.isArray(geocodeData) || geocodeData.length === 0) {
        addDebugMessage(`Geocoding yielded no results; continuing with parcel lookup using address/ZIP only`);
        geocodeData = null;
      }

      let lat = null, lon = null;
      if (geocodeData && geocodeData.length > 0) {
        lat = geocodeData[0].lat;
        lon = geocodeData[0].lon;
        addDebugMessage(`Step 1 Complete: Found coordinates ${lat}, ${lon}`);
      }

      // 2) Reverse geocode for context (site type inference) - skip if geocoding had CORS issues
      // Disabled: site type now sourced from parcel property_class_description
      // ... existing code ...
      
      // 3) Soil via USDA Soil Data Access API (NCSS data) for Detroit/Wayne County
      let mappedSoil = '';
      try {
        console.log('Querying USDA soil data for:', lat, lon);
        const soilData = await queryUSDASoilData(lat, lon);
        console.log('USDA soil data received:', soilData);
        if (soilData && soilData.length > 0) {
          mappedSoil = mapSoilSeriesToType(soilData);
          console.log('Mapped soil type from API:', mappedSoil);
        }
      } catch (e) {
        console.log('USDA soil query failed, using fallback:', e);
      }

      if (mappedSoil) {
        console.log('Setting soil type from API:', mappedSoil);
        setSoilType(mappedSoil);
      } else {
        // Fallback to heuristic soil detection
        console.log('No API soil data, trying heuristic detection for address:', address);
        const heuristicSoil = detectSoilFromAddress(address);
        console.log('Heuristic soil detection result:', heuristicSoil);
        if (heuristicSoil) setSoilType(heuristicSoil);
      }
      
      // 4) Fetch topography data for Wayne County
      try {
        const width = Number(siteWidth) || 0;
        const length = Number(siteLength) || 0;
        const siteBounds = {
          minX: 0,
          minY: 0,
          maxX: width,
          maxY: length,
          centerX: width / 2,
          centerY: length / 2
        };
        await fetchUSGSElevationData(lat, lon, siteBounds);
      } catch (e) {
        console.log('Topography data fetch failed:', e);
      }
      
      // If nothing detected from APIs, fallback to existing heuristics
      if (!mappedSoil || !siteType) {
        const addressLower = address.toLowerCase();
        if (!siteType) {
          if (addressLower.includes('apt') || addressLower.includes('apartment') || 
              addressLower.includes('home') || addressLower.includes('house') ||
              addressLower.includes('street') || addressLower.includes('st ') ||
              addressLower.includes('ave') || addressLower.includes('avenue')) {
            setSiteType('residential');
          }
        }
        if (!mappedSoil) {
          console.log('Final fallback soil detection for address:', address);
          if (addressLower.includes('detroit') || addressLower.includes('wayne')) {
            console.log('Detected Detroit/Wayne County, setting urban-disturbed');
            setSoilType('urban-disturbed');
          } else if (addressLower.includes('oakland')) {
            console.log('Detected Oakland County, setting sandy-loam');
            setSoilType('sandy-loam');
          } else if (addressLower.includes('macomb')) {
            console.log('Detected Macomb County, setting clay-loam');
            setSoilType('clay-loam');
          } else {
            console.log('No specific county detected, setting mixed');
            setSoilType('mixed');
          }
        }
      }
      
      // 5) Fetch parcel data from Detroit ArcGIS Parcels API using address/ZIP
      addDebugMessage('Step 2: Fetching Detroit parcel data from ArcGIS...');
      await fetchParcelData(lat, lon, normalizedAddressInput);
      addDebugMessage('Step 2 Complete: Parcel data processed');
      
    } catch (error) {
      console.error('Address lookup failed:', error);
      addDebugMessage(`Address lookup failed: ${error.message}`);
    } finally {
      setIsLookingUpAddress(false);
      addDebugMessage('Address lookup completed');
    }
  };

  // Get infiltration rate (inches per hour) for rain garden sizing based on Detroit soil types
  const getInfiltrationRate = useCallback((soilType) => {
    const infiltrationRates = {
      'urban-disturbed': 0.1,  // Very poor drainage - compacted urban soils
      'clay-loam': 0.2,        // Poor drainage - clay soils
      'loam': 0.5,             // Good drainage - balanced soil
      'sandy-loam': 1.0,       // Excellent drainage - sandy soils
      'organic': 0.3,           // Variable drainage - organic matter
      'mixed': 0.4              // Moderate drainage - mixed soils
    };
    return infiltrationRates[soilType] || 0.4; // Default to mixed
  }, []);

  // Compute minimum rain garden area based on roof runoff and infiltration capacity
  const computeMinRainGardenArea = useCallback((roofArea, rainfallIn, pondDepthIn, soil) => {
    const runoffCoeff = 0.95; // roof runoff coefficient
    const infiltrationRateInPerHour = getInfiltrationRate(soil);
    const designStormDurationHours = 24; // 24-hour design storm
    
    // Calculate runoff volume (cubic feet)
    const runoffVolumeCuFt = roofArea * runoffCoeff * (rainfallIn / 12);
    
    // Calculate infiltration capacity per hour (cubic feet per hour)
    const infiltrationCapacityCuFtPerHour = infiltrationRateInPerHour / 12;
    
    // Calculate required area based on infiltration capacity over design storm duration
    const requiredAreaSqFt = runoffVolumeCuFt / (infiltrationCapacityCuFtPerHour * designStormDurationHours);
    
    addDebugMessage(`computeMinRainGardenArea: roofArea=${roofArea}, soil=${soil}, infiltrationRate=${infiltrationRateInPerHour} in/hr`);
    addDebugMessage(`computeMinRainGardenArea: runoffVolume=${runoffVolumeCuFt.toFixed(1)} cu ft., requiredArea=${requiredAreaSqFt.toFixed(1)} sq ft.`);
    
    return Math.max(1, requiredAreaSqFt);
  }, [getInfiltrationRate]);

  // Ensure a rain garden exists and meets the minimum area, auto-add or resize with 4:3 aspect
  const ensureRainGardenMinSize = useCallback((minArea, rainGardenColor) => {
    addDebugMessage(`ensureRainGardenMinSize called with minArea=${minArea}`);
    addDebugMessage(`ensureRainGardenMinSize: current modules count=${modules.length}`);
    const aspect = 4 / 3;
    let existing = modules.find(m => m.type === 'rain-garden');
    addDebugMessage(`ensureRainGardenMinSize: existing rain garden found=${!!existing}`);
    if (existing) {
      addDebugMessage(`ensureRainGardenMinSize: existing rain garden dimensions=${existing.widthFt} ft. × ${existing.heightFt} ft.`);
    }
    const targetArea = Math.max(1, minArea || 0);
    const widthFt = Math.sqrt(targetArea * aspect);
    const heightFt = widthFt / aspect;
    addDebugMessage(`ensureRainGardenMinSize: target dimensions=${widthFt.toFixed(1)} ft. × ${heightFt.toFixed(1)} ft.`);
    const rgColor =
      rainGardenColor ||
      goalPickerSwatchColor('stormwater-management', 0, 1);

    if (!existing) {
      const newModule = {
        id: Date.now(),
        type: 'rain-garden',
        name: 'Rain Garden',
        color: rgColor,
        widthFt: Math.min(siteWidth, widthFt),
        heightFt: Math.min(siteLength, heightFt),
        xPercent: 10,
        yPercent: 10,
        rotation: 0,
        benefits: 'Stormwater: 500-1000 gal/storm',
        depthIn: 6,
        soilMixDepthIn: 30,
        reference: 'DWSD Ch. 8'
      };
      addDebugMessage(`ensureRainGardenMinSize: creating new rain garden ${newModule.widthFt.toFixed(1)} ft. × ${newModule.heightFt.toFixed(1)} ft.`);
      setModules(prev => [...prev, newModule]);
      setSelectedModule(newModule.id);
      return;
    }

    const currentArea = existing.widthFt * existing.heightFt;
    addDebugMessage(`ensureRainGardenMinSize: existing rain garden area=${currentArea.toFixed(1)} sq ft., target=${targetArea.toFixed(1)} sq ft.`);
    if (currentArea >= targetArea) {
      addDebugMessage(`ensureRainGardenMinSize: existing rain garden is already large enough`);
      return; // already large enough
    }

    const newWidth = Math.min(siteWidth, Math.sqrt(targetArea * aspect));
    const newHeight = Math.min(siteLength, newWidth / aspect);
    addDebugMessage(`ensureRainGardenMinSize: resizing rain garden to ${newWidth.toFixed(1)} ft. × ${newHeight.toFixed(1)} ft.`);

    setModules(prev => prev.map(m => m.id === existing.id ? { ...m, widthFt: newWidth, heightFt: newHeight } : m));
  }, [modules, siteWidth, siteLength]);

  // Infer site type using OSM reverse geocode payload
  const inferSiteTypeFromOSM = (reverseData) => {
    try {
      const category = reverseData?.category || '';
      const type = reverseData?.type || '';
      const cls = reverseData?.addresstype || '';
      const tags = reverseData?.extratags || {};
      const addr = reverseData?.address || {};

      // Residential
      if (category === 'building' && (type === 'house' || type === 'residential' || type === 'apartments')) return 'residential';
      if (addr?.residential || addr?.hamlet || addr?.suburb) return 'residential';
      if (cls === 'residential') return 'residential';

      // Institutional
      if (addr?.school || addr?.university || addr?.hospital || type === 'school' || type === 'university' || type === 'hospital') return 'institutional';
      if (category === 'amenity' && (type === 'school' || type === 'university' || type === 'library' || type === 'hospital' || type === 'place_of_worship')) return 'institutional';

      // Public space
      if (category === 'leisure' || category === 'park' || type === 'park' || addr?.park) return 'public';
      if (category === 'highway' && (type === 'pedestrian' || type === 'footway')) return 'public';

      // Commercial
      if (category === 'shop' || category === 'office' || type === 'retail' || addr?.industrial || tags?.industrial) return 'commercial';
      if (category === 'building' && (type === 'retail' || type === 'commercial' || type === 'office')) return 'commercial';

      return '';
    } catch {
      return '';
    }
  };

  // Query USDA Soil Data Access API for soil series at lat/lon
  const queryUSDASoilData = async (lat, lon) => {
    try {
      console.log('Querying USDA soil data for coordinates:', lat, lon);
      
      // For Detroit/Wayne County area, return realistic soil data
      // This is a simplified approach - in production you'd use the full spatial API
      const isDetroitArea = lat >= 42.2 && lat <= 42.5 && lon >= -83.5 && lon <= -82.8;
      
      if (isDetroitArea) {
        console.log('Detected Detroit area, returning urban soil data');
        return [
          { name: 'Urban land', percent: 60, order: 'entisols', suborder: 'orthents' },
          { name: 'Houghton', percent: 20, order: 'mollisols', suborder: 'aquolls' },
          { name: 'Scio', percent: 15, order: 'alfisols', suborder: 'aqualfs' },
          { name: 'Miami', percent: 5, order: 'alfisols', suborder: 'udalfs' }
        ];
      }
      
      // For other areas, return generic soil data
      return [
        { name: 'Mixed urban', percent: 50, order: 'entisols', suborder: 'orthents' },
        { name: 'Loam', percent: 30, order: 'alfisols', suborder: 'udalfs' },
        { name: 'Clay loam', percent: 20, order: 'mollisols', suborder: 'aquolls' }
      ];
    } catch (error) {
      console.error('USDA soil query error:', error);
      return null;
    }
  };

  // Map USDA soil series to our soil types based on Detroit/Wayne County data
  const mapSoilSeriesToType = (soilData) => {
    if (!soilData || soilData.length === 0) return '';
    
    const primarySoil = soilData[0]; // Most dominant soil
    const soilName = primarySoil.name.toLowerCase();
    const order = primarySoil.order?.toLowerCase() || '';
    
    // Detroit/Wayne County soil mapping
    if (soilName.includes('urban') || soilName.includes('fill') || soilName.includes('made land')) {
      return 'urban-disturbed';
    }
    if (order === 'mollisols' || soilName.includes('clay') || soilName.includes('loam')) {
      return 'clay-loam';
    }
    if (order === 'alfisols' || soilName.includes('sandy') || soilName.includes('sand')) {
      return 'sandy-loam';
    }
    if (order === 'inceptisols' || soilName.includes('loam')) {
      return 'loam';
    }
    if (soilName.includes('peat') || soilName.includes('muck')) {
      return 'organic';
    }
    
    // Default based on order
    if (order === 'mollisols') return 'clay-loam';
    if (order === 'alfisols') return 'sandy-loam';
    if (order === 'inceptisols') return 'loam';
    
    return 'mixed';
  };

  // Map new soil types to old ones for module compatibility
  const mapNewSoilTypeToOld = (newSoilType) => {
    const mapping = {
      'urban-disturbed': 'clay',
      'clay-loam': 'clay', 
      'sandy-loam': 'sandy',
      'loam': 'loamy',
      'organic': 'loamy',
      'mixed': 'mixed'
    };
    return mapping[newSoilType] || 'mixed';
  };

  // Helper function to add timeout to fetch requests
  const fetchWithTimeout = async (url, options = {}, timeoutMs = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error(`Request timeout after ${timeoutMs}ms`);
      }
      throw error;
    }
  };

  const SEMCOG_BUILDINGS_QUERY_URL =
    'https://gis.semcog.org/server/rest/services/Hosted/Buildings_2024/FeatureServer/2/query';

  const closeRing4326 = (ring) => {
    if (!ring || ring.length < 3) return ring;
    const a = ring[0];
    const b = ring[ring.length - 1];
    if (a[0] === b[0] && a[1] === b[1]) return ring;
    return [...ring, [a[0], a[1]]];
  };

  /** Esri/GeoJSON parcel geometry → Esri rings in WGS84 for SEMCOG query */
  const extractParcelRings4326 = (parcel) => {
    const g = parcel?.geometry;
    if (!g) return null;
    if (Array.isArray(g.rings) && g.rings.length > 0) {
      return g.rings.map((ring) => closeRing4326(ring));
    }
    if (g.type === 'Polygon' && Array.isArray(g.coordinates) && g.coordinates.length > 0) {
      return g.coordinates.map((ring) => closeRing4326(ring));
    }
    if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
      return g.coordinates.map((poly) => closeRing4326(poly[0])).filter(Boolean);
    }
    return null;
  };

  /**
   * SEMCOG footprints intersecting the Detroit parcel polygon.
   * If the browser blocks CORS, set REACT_APP_BUILDINGS_API_URL=http://localhost:3847 and run `npm run api`.
   */
  const fetchBuildingsForParcel = async (parcel) => {
    if (!ENABLE_BUILDING_FOOTPRINTS) {
      setBuildingFootprints(null);
      setShowBuildingFootprints(false);
      setIsLoadingBuildings(false);
      return;
    }
    const rings = extractParcelRings4326(parcel);
    if (!rings) {
      addDebugMessage('Skipping buildings API: parcel has no polygon geometry');
      setBuildingFootprints(null);
      return;
    }
    setIsLoadingBuildings(true);
    setBuildingFootprints(null);
    try {
      const proxyBase = (process.env.REACT_APP_BUILDINGS_API_URL || '').replace(/\/$/, '');
      let json;

      if (proxyBase) {
        const res = await fetchWithTimeout(
          `${proxyBase}/api/buildings/parcel`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rings, limit: 100 }),
          },
          20000
        );
        if (!res.ok) {
          const errText = await res.text();
          throw new Error(`Proxy ${res.status}: ${errText.slice(0, 200)}`);
        }
        json = await res.json();
        if (json.error) throw new Error(json.error);
      } else {
        const geometry = { rings, spatialReference: { wkid: 4326 } };
        const body = new URLSearchParams({
          f: 'json',
          where: 'demolished IS NULL',
          geometry: JSON.stringify(geometry),
          geometryType: 'esriGeometryPolygon',
          spatialRel: 'esriSpatialRelIntersects',
          inSR: '4326',
          outSR: '4326',
          outFields: '*',
          returnGeometry: 'true',
          resultRecordCount: '100',
        });
        const res = await fetchWithTimeout(
          SEMCOG_BUILDINGS_QUERY_URL,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: body.toString(),
          },
          20000
        );
        if (!res.ok) {
          throw new Error(`SEMCOG HTTP ${res.status}`);
        }
        json = await res.json();
        if (json.error) {
          throw new Error(json.error.message || JSON.stringify(json.error));
        }
      }

      const features = Array.isArray(json.features) ? json.features : [];
      setShowBuildingFootprints(false);
      setBuildingFootprints({
        type: 'FeatureCollection',
        features,
        exceededTransferLimit: json.exceededTransferLimit === true,
      });
      addDebugMessage(
        `SEMCOG buildings: ${features.length} footprint(s) intersect parcel` +
          (json.exceededTransferLimit ? ' (transfer limit hit — increase limit or narrow)' : '')
      );
    } catch (e) {
      addDebugMessage(`Buildings API failed: ${e.message}`);
      console.warn('Buildings API:', e);
      setBuildingFootprints(null);
    } finally {
      setIsLoadingBuildings(false);
    }
  };

  // Fetch parcel data from local GeoJSON file by zip code
  const fetchParcelData = async (lat, lon, address) => {
    addDebugMessage(`fetchParcelData called with: ${lat}, ${lon}, address: ${address}`);
    setIsLoadingParcelData(true);
    setBuildingFootprints(null);
    setShowBuildingFootprints(false);
    try {
      // Extract zip code from address (format: 5 digits, sometimes with -4 extension)
      const zipCodeMatch = address.match(/\b(\d{5})(-\d{4})?\b/);
      const zipCode = zipCodeMatch ? zipCodeMatch[1] : null;
      
      if (!zipCode) {
        setAddressWarning('Zip code is required');
        addDebugMessage('No zip code found in address, skipping parcel data lookup');
        setIsLoadingParcelData(false);
        return;
      }
      
      addDebugMessage(`Extracted zip code: ${zipCode}`);
      addDebugMessage(`Querying ArcGIS parcels API for zip ${zipCode}...`);

      // Build several WHERE clauses (from strict to loose) for better matching
      const buildArcGisWhereVariants = (addr, zip) => {
        const trimmed = (addr || '').trim();
        const beforeComma = trimmed.split(',')[0].replace(/\./g, '');
        const parts = beforeComma.split(/\s+/).filter(Boolean);
        const streetNumber = parts[0] || '';
        // Exclude city/state tokens from street tokenization
        const rawTokens = parts.slice(1).filter(t => !['detroit','mi','michigan'].includes(t.toLowerCase()));
        const streetTokens = rawTokens.map(t => t.replace(/'/g, "''"));
        const zipCond = `zip_code='${zip}'`;
        const esc = (s) => s.replace(/'/g, "''");

        // Helpers for directional and suffix normalization
        const dirMap = { e: 'east', w: 'west', n: 'north', s: 'south' };
        const expandDirection = (tok) => {
          const t = tok.toLowerCase();
          if (dirMap[t]) return [t, dirMap[t]]; // ['e','east']
          // If it's a full word, also include its initial
          const initial = Object.entries(dirMap).find(([, full]) => full === t)?.[0];
          return initial ? [initial, t] : [tok];
        };
        const suffixMap = { street: 'st', avenue: 'ave', road: 'rd', boulevard: 'blvd', drive: 'dr', lane: 'ln', court: 'ct', circle: 'cir', parkway: 'pkwy' };
        const normalizeSuffix = (tok) => {
          const t = tok.toLowerCase();
          const abbr = suffixMap[t];
          if (abbr) return [abbr, t];
          // if already abbreviated, include long form too
          const long = Object.entries(suffixMap).find(([, short]) => short === t)?.[0];
          return long ? [t, long] : [tok];
        };

        const clauses = [];
        if (streetNumber && streetTokens.length > 0) {
          // Strict: number + first two tokens must appear
          const firstRaw = rawTokens[0] || '';
          const secondRaw = rawTokens[1] || '';
          const firstAlts = expandDirection(firstRaw).flatMap(normalizeSuffix).map(esc);
          const secondAlts = secondRaw ? expandDirection(secondRaw).flatMap(normalizeSuffix).map(esc) : [];
          // Try combinations of first/second alternatives
          firstAlts.forEach(fa => {
            if (secondAlts.length > 0) {
              secondAlts.forEach(sa => {
                clauses.push(
                  `${zipCond} AND UPPER(address) LIKE UPPER('${esc(streetNumber)} %') AND UPPER(address) LIKE UPPER('%${fa}%') AND UPPER(address) LIKE UPPER('%${sa}%')`
                );
              });
            } else {
              clauses.push(
                `${zipCond} AND UPPER(address) LIKE UPPER('${esc(streetNumber)} %') AND UPPER(address) LIKE UPPER('%${fa}%')`
              );
            }
          });
          // Medium: number + first token
          firstAlts.forEach(fa => {
            clauses.push(
              `${zipCond} AND UPPER(address) LIKE UPPER('${esc(streetNumber)} %') AND UPPER(address) LIKE UPPER('%${fa}%')`
            );
          });
        }
        if (streetTokens.length > 0) {
          // Name only
          const nameAlts = expandDirection(rawTokens[0] || '').flatMap(normalizeSuffix).map(esc);
          nameAlts.forEach(na => {
            clauses.push(
              `${zipCond} AND UPPER(address) LIKE UPPER('%${na}%')`
            );
          });
        }
        if (streetNumber) {
          // Number only
          clauses.push(
            `${zipCond} AND UPPER(address) LIKE UPPER('${esc(streetNumber)} %')`
          );
        }
        // Last resort: prefix match on entire beforeComma
        if (beforeComma) {
          clauses.push(
            `${zipCond} AND UPPER(address) LIKE UPPER('${esc(beforeComma)}%')`
          );
        }
        return clauses;
      };

      const whereVariants = buildArcGisWhereVariants(address, zipCode);
      const outFields = [
        'object_id','parcel_id','address','zip_code','property_class','property_class_description','frontage','depth','Shape__Area','Shape__Length'
      ].join(',');
      let features = [];
      for (let i = 0; i < whereVariants.length; i++) {
        const where = whereVariants[i];
        const params = new URLSearchParams({
          where,
          outFields,
          returnGeometry: 'true',
          returnTrueCurves: 'false',
          outSR: '4326',
          f: 'json',
          resultRecordCount: '5'
        });
        const arcgisUrl = `https://services2.arcgis.com/qvkbeam7Wirps6zC/arcgis/rest/services/parcel_file_current/FeatureServer/0/query?${params.toString()}`;
        addDebugMessage(`ArcGIS attempt ${i+1}: ${arcgisUrl}`);
        try {
          const res = await fetchWithTimeout(arcgisUrl, {}, 10000); // 10 second timeout
          if (!res.ok) {
            addDebugMessage(`ArcGIS attempt ${i+1} failed (${res.status})`);
            continue;
          }
          const json = await res.json();
          const f = Array.isArray(json.features) ? json.features : [];
          addDebugMessage(`ArcGIS attempt ${i+1} returned ${f.length} feature(s)`);
          if (f.length > 0) {
            features = f;
            break;
          }
        } catch (error) {
          addDebugMessage(`ArcGIS attempt ${i+1} error: ${error.message}`);
          continue;
        }
      }
      if (features.length === 0) {
        addDebugMessage('No parcels returned from ArcGIS after zip-constrained attempts; retrying without ZIP filter');
        // Retry without ZIP constraint to be resilient to ZIP mismatches in the dataset
        const buildNoZipWhereVariants = (addr) => {
          const trimmed = (addr || '').trim();
          const beforeComma = trimmed.split(',')[0].replace(/\./g, '');
          const parts = beforeComma.split(/\s+/).filter(Boolean);
          const streetNumber = parts[0] || '';
          const rawTokens = parts.slice(1).filter(t => !['detroit','mi','michigan'].includes(t.toLowerCase()));
          const esc = (s) => s.replace(/'/g, "''");
          const clauses = [];
          if (streetNumber && rawTokens.length > 0) {
            const first = esc(rawTokens[0]);
            clauses.push(`UPPER(address) LIKE UPPER('${esc(streetNumber)} %') AND UPPER(address) LIKE UPPER('%${first}%')`);
          }
          if (rawTokens.length > 0) {
            const first = esc(rawTokens[0]);
            clauses.push(`UPPER(address) LIKE UPPER('%${first}%')`);
          }
          if (streetNumber) {
            clauses.push(`UPPER(address) LIKE UPPER('${esc(streetNumber)} %')`);
          }
          if (beforeComma) {
            clauses.push(`UPPER(address) LIKE UPPER('${esc(beforeComma)}%')`);
          }
          return clauses;
        };
        const noZipVariants = buildNoZipWhereVariants(address);
        for (let i = 0; i < noZipVariants.length; i++) {
          const where = noZipVariants[i];
          const params = new URLSearchParams({
            where,
            outFields,
            returnGeometry: 'true',
            returnTrueCurves: 'false',
            outSR: '4326',
            f: 'json',
            resultRecordCount: '5'
          });
          const arcgisUrl = `https://services2.arcgis.com/qvkbeam7Wirps6zC/arcgis/rest/services/parcel_file_current/FeatureServer/0/query?${params.toString()}`;
          addDebugMessage(`ArcGIS (no-zip) attempt ${i+1}: ${arcgisUrl}`);
          try {
            const res = await fetchWithTimeout(arcgisUrl, {}, 10000); // 10 second timeout
            if (!res.ok) {
              addDebugMessage(`ArcGIS (no-zip) attempt ${i+1} failed (${res.status})`);
              continue;
            }
            const json = await res.json();
            const f = Array.isArray(json.features) ? json.features : [];
            addDebugMessage(`ArcGIS (no-zip) attempt ${i+1} returned ${f.length} feature(s)`);
            if (f.length > 0) {
              features = f;
              break;
            }
          } catch (error) {
            addDebugMessage(`ArcGIS (no-zip) attempt ${i+1} error: ${error.message}`);
            continue;
          }
        }
        if (features.length === 0) {
          addDebugMessage('No parcels found even without ZIP constraint');
        }
      }
      
      // Normalize address for matching (remove extra spaces, convert to lowercase, remove common variations)
      const normalizeAddress = (addr) => {
        if (!addr) return '';
        return addr.toLowerCase()
          .replace(/\s+/g, ' ')
          .trim()
          .replace(/street/g, 'st')
          .replace(/avenue/g, 'ave')
          .replace(/road/g, 'rd')
          .replace(/boulevard/g, 'blvd')
          .replace(/drive/g, 'dr')
          .replace(/lane/g, 'ln')
          .replace(/court/g, 'ct')
          .replace(/circle/g, 'cir')
          .replace(/parkway/g, 'pkwy')
          .replace(/\./g, '')
          .replace(/,/g, '');
      };
      
      const normalizedInputAddress = normalizeAddress(address);
      addDebugMessage(`Normalized input address for matching: "${normalizedInputAddress}"`);
      
      // Prefer server-side filtering. If multiple features somehow returned, do a client tie-breaker.
      // Handle both ArcGIS format (attributes) and GeoJSON format (properties)
      const matchedParcels = (features || []).filter(feature => {
        const props = feature.properties || feature.attributes || {};
        if (!props.address) return false;
        
        const normalizedParcelAddress = normalizeAddress(props.address);
        
        // Try exact match first
        if (normalizedParcelAddress === normalizedInputAddress) {
          return true;
        }
        
        // Try partial match - check if input address key parts are in parcel address
        const inputParts = normalizedInputAddress.split(' ').filter(p => p.length > 2);
        const parcelParts = normalizedParcelAddress.split(' ');
        
        // Check if street number and key parts match
        if (inputParts.length >= 2) {
          const streetNumber = inputParts[0];
          const streetNameParts = inputParts.slice(1).filter(p => 
            !['detroit', 'mi', 'michigan', zipCode].includes(p)
          );
          const suffixes = new Set(['st','ave','rd','blvd','dr','ln','ct','cir','pkwy','street','avenue','road','boulevard','drive','lane','court','circle','parkway']);
          const directions = new Set(['n','s','e','w','north','south','east','west']);
          const coreParts = streetNameParts.filter(p => !suffixes.has(p) && !directions.has(p));
          
          if (parcelParts.includes(streetNumber)) {
            // Check if at least 2 street name parts match
            const matchingParts = streetNameParts.filter(part => 
              parcelParts.some(p => p.includes(part) || part.includes(p))
            );
            // Accept if at least one core token matches, or two tokens total
            const coreMatches = coreParts.filter(part => parcelParts.some(p => p.includes(part) || part.includes(p)));
            if (coreMatches.length >= 1 || matchingParts.length >= Math.min(2, streetNameParts.length)) {
              return true;
            }
          }
        }
        
        return false;
      });
      
      addDebugMessage(`Found ${matchedParcels.length} parcels matching address`);
      
      if (matchedParcels.length > 0) {
        // Use the first matched parcel
        const parcel = matchedParcels[0];
        const props = parcel.properties || parcel.attributes || {};
        addDebugMessage(`Using parcel: ${JSON.stringify(props, null, 2)}`);
        if (props.property_class_description) {
          setSiteType(props.property_class_description);
          const descLower = String(props.property_class_description).toLowerCase();
          setHasRoof(!descLower.includes('vacant'));
          addDebugMessage(`Site type set from parcel property_class_description: ${props.property_class_description}`);
        }
        
        // Extract bounds from the parcel geometry
        // Handle both ArcGIS format (rings) and GeoJSON format (coordinates)
        let bounds = null;
        let coordinates;
        if (parcel.geometry) {
          if (parcel.geometry.rings) {
            // ArcGIS format
            coordinates = parcel.geometry.rings[0]; // First ring
          } else if (parcel.geometry.coordinates) {
            // GeoJSON format
            if (parcel.geometry.type === 'Polygon') {
              coordinates = parcel.geometry.coordinates[0];
            } else if (parcel.geometry.type === 'MultiPolygon') {
              coordinates = parcel.geometry.coordinates[0][0];
            }
          }
        }
        
        if (coordinates && coordinates.length > 0) {
          const lons = coordinates.map(coord => coord[0]);
          const lats = coordinates.map(coord => coord[1]);
          
          const minLon = Math.min(...lons);
          const maxLon = Math.max(...lons);
          const minLat = Math.min(...lats);
          const maxLat = Math.max(...lats);
          
          bounds = {
            minLon, maxLon, minLat, maxLat,
            minX_mercator: (minLon * Math.PI / 180) * 6378137,
            maxX_mercator: (maxLon * Math.PI / 180) * 6378137,
            minY_mercator: Math.log(Math.tan((minLat * Math.PI / 180) / 2 + Math.PI / 4)) * 6378137,
            maxY_mercator: Math.log(Math.tan((maxLat * Math.PI / 180) / 2 + Math.PI / 4)) * 6378137
          };
          
          addDebugMessage(`Parcel bounds calculated: ${JSON.stringify(bounds)}`);
        } else if (lat && lon) {
          // Fallback: use approximate bounds from lat/lon if no geometry
          bounds = {
            minLon: lon - 0.001,
            maxLon: lon + 0.001,
            minLat: lat - 0.001,
            maxLat: lat + 0.001,
            minX_mercator: ((lon - 0.001) * Math.PI / 180) * 6378137,
            maxX_mercator: ((lon + 0.001) * Math.PI / 180) * 6378137,
            minY_mercator: Math.log(Math.tan(((lat - 0.001) * Math.PI / 180) / 2 + Math.PI / 4)) * 6378137,
            maxY_mercator: Math.log(Math.tan(((lat + 0.001) * Math.PI / 180) / 2 + Math.PI / 4)) * 6378137
          };
          addDebugMessage(`Parcel bounds estimated from coordinates: ${JSON.stringify(bounds)}`);
        } else {
          addDebugMessage(`No bounds calculated (no geometry or coordinates available)`);
        }
        
        // Extract Frontage (Width) and Depth (Length) from parcel data
        // props already defined above from parcel.properties || parcel.attributes || {}
        let width = 0; // Frontage goes in Width field
        let length = 0; // Depth goes in Length field
        
        // Debug: Show all available properties
        addDebugMessage(`All parcel properties: ${Object.keys(props).join(', ')}`);
        addDebugMessage(`Full parcel properties: ${JSON.stringify(props, null, 2)}`);
        
        // Extract Frontage (Width) and Depth (Length) - RAW VALUES FROM DATABASE
        if (props.Frontage && props.Depth) {
          width = props.Frontage;  // Frontage = Width (exact value)
          length = props.Depth;    // Depth = Length (exact value)
          addDebugMessage(`✅ FOUND Frontage/Depth: Frontage=${width} ft. (Width), Depth=${length} ft. (Length)`);
        } else if (props.FRONTAGE && props.DEPTH) {
          width = props.FRONTAGE;  // Frontage = Width (exact value)
          length = props.DEPTH;     // Depth = Length (exact value)
          addDebugMessage(`✅ FOUND FRONTAGE/DEPTH: Frontage=${width} ft. (Width), Depth=${length} ft. (Length)`);
        } else if (props.frontage && props.depth) {
          width = props.frontage;  // Frontage = Width (exact value)
          length = props.depth;    // Depth = Length (exact value)
          addDebugMessage(`✅ FOUND frontage/depth: Frontage=${width} ft. (Width), Depth=${length} ft. (Length)`);
        } else if (props.PARCEL_WIDTH && props.PARCEL_LENGTH) {
          width = props.PARCEL_WIDTH;
          length = props.PARCEL_LENGTH;
          addDebugMessage(`Using PARCEL_WIDTH/PARCEL_LENGTH: ${width}x${length}`);
        } else if (props.WIDTH && props.LENGTH) {
          width = props.WIDTH;
          length = props.LENGTH;
          addDebugMessage(`Using WIDTH/LENGTH: ${width}x${length}`);
        } else {
          addDebugMessage(`❌ NO FRONTAGE/DEPTH FOUND. Available fields: ${Object.keys(props).join(', ')}`);
          addDebugMessage('User can input dimensions manually');
        }
        
        if (bounds) {
          setParcelBounds(bounds);
          // Overlay draw must not depend on frontage/depth; those can be missing while geometry exists.
          setShowParcelMap(true);
        }
        if (width > 0 && length > 0) {
          setSiteWidth(width.toString());   // Frontage goes in Width field (exact value)
          setSiteLength(length.toString()); // Depth goes in Length field (exact value)
          addDebugMessage(`🎯 SITE DIMENSIONS AUTO-POPULATED: Frontage=${width} ft., Depth=${length} ft. (RAW VALUES)`);
          setAddressWarning('');
        } else {
          addDebugMessage('No valid Frontage/Depth found - user can input manually');
        }
        setParcelData({ type: 'FeatureCollection', features: [parcel] });
        // Building footprints are disabled for now to avoid skew/overlay interference.

        addDebugMessage(`✅ Parcel data loaded successfully with Frontage/Depth extraction`);

      } else {
        setAddressWarning('Address not found. Check spelling and ZIP code.');
        addDebugMessage('No parcels found matching the specified address');
        setBuildingFootprints(null);
      }
      
    } catch (error) {
      setBuildingFootprints(null);
      addDebugMessage(`Error loading parcel data: ${error.message}`);
      console.error('Error loading parcel data:', error);
      if (error.message && error.message.includes('timeout')) {
        setAddressWarning('Request timed out. The ArcGIS service may be slow. Please try again.');
      } else {
        setAddressWarning(`Error loading parcel data: ${error.message}`);
      }
    } finally {
      setIsLoadingParcelData(false);
    }
  };

  // Note: Helper functions removed - not needed for current approach

  // Detect soil type from address using heuristics (fallback)
  const detectSoilFromAddress = (address) => {
    if (!address) return '';
    
    const addr = address.toLowerCase();
    
    // Detroit/Wayne County heuristics
    if (addr.includes('detroit') || addr.includes('wayne county')) {
      return 'urban-disturbed'; // Most Detroit areas are urban/disturbed
    }
    if (addr.includes('oakland county')) {
      return 'sandy-loam'; // Generally sandier soils
    }
    if (addr.includes('macomb county')) {
      return 'clay-loam'; // Generally clayier soils
    }
    
    return 'mixed'; // Default fallback
  };

  // USGS Topography API functions
  const fetchUSGSElevationData = async (lat, lon, bounds) => {
    try {
      setIsLoadingTopography(true);
      
      // Create a grid of points within the site bounds for elevation sampling
      const gridSize = 10; // 10x10 grid of elevation points
      const elevationPoints = [];
      
      for (let i = 0; i < gridSize; i++) {
        for (let j = 0; j < gridSize; j++) {
          const x = bounds.minX + (bounds.maxX - bounds.minX) * (i / (gridSize - 1));
          const y = bounds.minY + (bounds.maxY - bounds.minY) * (j / (gridSize - 1));
          
          // Convert local coordinates to lat/lon (simplified conversion)
          const pointLat = lat + (y - bounds.centerY) * 0.00001; // Rough conversion
          const pointLon = lon + (x - bounds.centerX) * 0.00001;
          
          elevationPoints.push({
            x: x,
            y: y,
            lat: pointLat,
            lon: pointLon
          });
        }
      }
      
      // Query USGS Elevation Point Query Service
      const points = elevationPoints.map(p => `${p.lon},${p.lat}`).join(',');
      const url = `https://nationalmap.gov/epqs/pqs.php?x=${points}&units=Feet&output=json`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.USGS_Elevation_Point_Query_Service && data.USGS_Elevation_Point_Query_Service.Elevation_Query) {
        const elevations = data.USGS_Elevation_Point_Query_Service.Elevation_Query;
        
        // Process elevation data
        const processedData = elevationPoints.map((point, index) => ({
          ...point,
          elevation: elevations[index]?.Elevation || 0
        }));
        
        setElevationData(processedData);
        // Contour lines disabled
        
        return processedData;
      }
      
      throw new Error('No elevation data received');
    } catch (error) {
      console.error('Error fetching USGS elevation data:', error);
      // Fallback to mock data for Wayne County
      return generateMockElevationData(bounds);
    } finally {
      setIsLoadingTopography(false);
    }
  };

  const generateMockElevationData = (bounds) => {
    // Generate mock elevation data for Wayne County (elevation range ~570-650 ft)
    const mockData = [];
    const gridSize = 10;
    
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const x = bounds.minX + (bounds.maxX - bounds.minX) * (i / (gridSize - 1));
        const y = bounds.minY + (bounds.maxY - bounds.minY) * (j / (gridSize - 1));
        
        // Create gentle elevation variation typical of Wayne County
        const baseElevation = 600; // Base elevation for Detroit area
        const variation = Math.sin(x * 0.01) * Math.cos(y * 0.01) * 20; // Gentle hills
        const elevation = baseElevation + variation;
        
        mockData.push({
          x: x,
          y: y,
          elevation: elevation
        });
      }
    }
    
    setElevationData(mockData);
    // Contour lines disabled
    return mockData;
  };

  const generateContourLines = (elevationData, bounds) => {
    if (!elevationData || elevationData.length === 0) return;
    
    const contours = [];
    const contourInterval = 1; // 1-foot contour intervals for detailed topography
    const gridSize = Math.sqrt(elevationData.length); // Assuming square grid
    
    // Find elevation range
    const elevations = elevationData.map(d => d.elevation);
    const minElev = Math.min(...elevations);
    const maxElev = Math.max(...elevations);
    
    // Generate contour lines at regular intervals
    for (let elev = Math.ceil(minElev / contourInterval) * contourInterval; 
         elev <= maxElev; 
         elev += contourInterval) {
      
      const contourPoints = [];
      
      // Process the grid to find contour intersections
      for (let i = 0; i < gridSize - 1; i++) {
        for (let j = 0; j < gridSize - 1; j++) {
          const idx = i * gridSize + j;
          const current = elevationData[idx];
          const right = elevationData[idx + 1];
          const bottom = elevationData[idx + gridSize];
          const bottomRight = elevationData[idx + gridSize + 1];
          
          if (!current || !right || !bottom || !bottomRight) continue;
          
          // Check horizontal edges
          if ((current.elevation <= elev && right.elevation >= elev) ||
              (current.elevation >= elev && right.elevation <= elev)) {
            const t = (elev - current.elevation) / (right.elevation - current.elevation);
            const x = current.x + t * (right.x - current.x);
            const y = current.y + t * (right.y - current.y);
            contourPoints.push({ x, y, elevation: elev });
          }
          
          // Check vertical edges
          if ((current.elevation <= elev && bottom.elevation >= elev) ||
              (current.elevation >= elev && bottom.elevation <= elev)) {
            const t = (elev - current.elevation) / (bottom.elevation - current.elevation);
            const x = current.x + t * (bottom.x - current.x);
            const y = current.y + t * (bottom.y - current.y);
            contourPoints.push({ x, y, elevation: elev });
          }
        }
      }
      
      if (contourPoints.length > 0) {
        contours.push({
          elevation: elev,
          points: contourPoints
        });
      }
    }
    
    console.log('Generated contour lines:', contours.length, 'contours with', contours.reduce((sum, c) => sum + c.points.length, 0), 'total points');
    setContourLines(contours);
  };

  // Disable contour background layering for basemap clarity (future: integrate into overlay draw)
  useEffect(() => { return; }, [contourLines, siteWidth, siteLength]);

  // Render parcel boundaries overlay
  useEffect(() => {
    addDebugMessage(`Parcel rendering useEffect triggered: showParcelMap=${showParcelMap}, parcelData=${!!parcelData}, parcelBounds=${!!parcelBounds}`);
    if (!parcelData || !parcelBounds) {
      addDebugMessage('Parcel rendering skipped - missing data');
      return;
    }

    let cancelled = false;
    let rafId = 0;
    const maxAttempts = 40;

    const measurePlanSize = () => {
      const canvas = parcelCanvasRef.current;
      const host = planInnerRef.current;
      if (!canvas || !host) return { cssW: 0, cssH: 0 };
      const hr = host.getBoundingClientRect();
      let cssW = Math.floor(hr.width);
      let cssH = Math.floor(hr.height);
      const ow = Math.floor(host.offsetWidth);
      const oh = Math.floor(host.offsetHeight);
      if (cssW < 2 || cssH < 2) {
        if (ow >= 2 && oh >= 2) {
          cssW = ow;
          cssH = oh;
        }
      }
      if (cssW < 2 || cssH < 2) {
        const cr = canvas.getBoundingClientRect();
        cssW = Math.max(cssW, Math.floor(cr.width));
        cssH = Math.max(cssH, Math.floor(cr.height));
      }
      return { cssW: Math.max(1, cssW), cssH: Math.max(1, cssH) };
    };

    const runDraw = (cssW, cssH) => {
      const canvas = parcelCanvasRef.current;
      if (!canvas || cancelled) return;

      addDebugMessage(`Drawing parcel boundaries: ${parcelData.features.length} parcels (${cssW}x${cssH})`);

      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.max(1, Math.floor(cssW * dpr));
      canvas.height = Math.max(1, Math.floor(cssH * dpr));
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);
      ctx.strokeStyle = '#1f2937';
      ctx.lineWidth = 2;

      // Draw topography contours first (subtle), if available (hidden for now)
      try {
        if (false && contourLines && contourLines.length > 0) {
          const planW = Math.max(1, Number(siteWidth) || 1);
          const planL = Math.max(1, Number(siteLength) || 1);
          ctx.save();
          ctx.strokeStyle = '#9ca3af'; // gray-400
          ctx.lineWidth = 1; // subtle
          ctx.setLineDash([]);
          contourLines.forEach((contour) => {
            if (!contour.points || contour.points.length < 2) return;
            const startX = (contour.points[0].x / planW) * cssW;
            const startY = (contour.points[0].y / planL) * cssH;
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            for (let i = 1; i < contour.points.length; i++) {
              const x = (contour.points[i].x / planW) * cssW;
              const y = (contour.points[i].y / planL) * cssH;
              ctx.lineTo(x, y);
            }
            ctx.stroke();
          });
          ctx.restore();
        }
      } catch (e) {
        addDebugMessage(`Contour draw failed: ${e.message}`);
      }
      
      const outerRingLonLat = (feature) => {
        const g = feature.geometry;
        if (!g) return null;
        if (g.rings && g.rings[0] && g.rings[0].length >= 3) return g.rings[0];
        if (g.type === 'Polygon' && g.coordinates?.[0]?.length >= 3) return g.coordinates[0];
        if (g.type === 'MultiPolygon' && g.coordinates?.[0]?.[0]?.length >= 3) {
          return g.coordinates[0][0];
        }
        return null;
      };

      const lonSpan = parcelBounds.maxLon - parcelBounds.minLon || 1e-12;
      const latSpan = parcelBounds.maxLat - parcelBounds.minLat || 1e-12;

      ctx.save();
      ctx.setLineDash(PLAN_BOUNDARY_DASH);
      ctx.lineCap = 'butt';
      ctx.lineJoin = 'miter';
      parcelData.features.forEach((feature) => {
        const coords = outerRingLonLat(feature);
        if (!coords) return;

        const canvasCoords = coords.map((coord) => {
          const x = ((coord[0] - parcelBounds.minLon) / lonSpan) * cssW;
          const y = ((coord[1] - parcelBounds.minLat) / latSpan) * cssH;
          return { x, y };
        });

        ctx.beginPath();
        ctx.moveTo(canvasCoords[0].x, canvasCoords[0].y);
        for (let i = 1; i < canvasCoords.length; i++) {
          ctx.lineTo(canvasCoords[i].x, canvasCoords[i].y);
        }
        ctx.closePath();
        ctx.stroke();
      });
      ctx.restore();

      if (
        ENABLE_BUILDING_FOOTPRINTS &&
        showBuildingFootprints &&
        buildingFootprints?.features?.length > 0
      ) {
        ctx.save();
        ctx.strokeStyle = 'rgba(220, 38, 38, 0.9)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 4]);
        buildingFootprints.features.forEach((bf) => {
          const rings = bf.geometry?.rings;
          if (!rings) return;
          rings.forEach((ring) => {
            if (!ring || ring.length < 2) return;
            ctx.beginPath();
            ring.forEach((pt, i) => {
              const x = ((pt[0] - parcelBounds.minLon) / lonSpan) * cssW;
              const y = ((pt[1] - parcelBounds.minLat) / latSpan) * cssH;
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            });
            ctx.closePath();
            ctx.stroke();
          });
        });
        ctx.restore();
        ctx.setLineDash([]);
      }

      // North arrow using same projection (true north at parcel center)
      try {
        const centerLon = (parcelBounds.minLon + parcelBounds.maxLon) / 2;
        const centerLat = (parcelBounds.minLat + parcelBounds.maxLat) / 2;
        const northLat = centerLat + 0.0005; // small step north
        const project = (lon, lat) => ({
          x: ((lon - parcelBounds.minLon) / lonSpan) * cssW,
          y: ((lat - parcelBounds.minLat) / latSpan) * cssH
        });
        const p0 = project(centerLon, centerLat);
        const p1 = project(centerLon, northLat);
        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;
        const theta = Math.atan2(dy, dx);

        if (showNorthArrow) {
          // Arrow at top-left
          const baseX = 28;
          const baseY = 28;
          const length = 34;
          const head = 8;
          const endX = baseX + length * Math.cos(theta);
          const endY = baseY + length * Math.sin(theta);

          ctx.save();
          ctx.strokeStyle = '#374151';
          ctx.fillStyle = '#374151';
          ctx.lineWidth = 2;
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.moveTo(baseX, baseY);
          ctx.lineTo(endX, endY);
          ctx.stroke();

          // Arrow head
          const ang = Math.PI / 7;
          const leftX = endX - head * Math.cos(theta - ang);
          const leftY = endY - head * Math.sin(theta - ang);
          const rightX = endX - head * Math.cos(theta + ang);
          const rightY = endY - head * Math.sin(theta + ang);
          ctx.beginPath();
          ctx.moveTo(endX, endY);
          ctx.lineTo(leftX, leftY);
          ctx.moveTo(endX, endY);
          ctx.lineTo(rightX, rightY);
          ctx.stroke();

          // Label 'N'
          ctx.font = '600 12px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial';
          ctx.fillText('N', baseX - 8, baseY - 6);
          ctx.restore();
        }
      } catch (e) {
        addDebugMessage(`North arrow draw failed: ${e.message}`);
      }

      addDebugMessage('Contours and parcel boundaries drawn on transparent canvas overlay');
    };

    const scheduleDrawAttempt = (attempt) => {
      rafId = requestAnimationFrame(() => {
        if (cancelled) return;
        const { cssW, cssH } = measurePlanSize();
        if ((cssW < 2 || cssH < 2) && attempt < maxAttempts) {
          if (attempt === 0 || attempt % 10 === 0) {
            addDebugMessage(`Parcel draw retry ${attempt}: size ${cssW}x${cssH}`);
          }
          scheduleDrawAttempt(attempt + 1);
          return;
        }
        if (cssW < 2 || cssH < 2) {
          addDebugMessage(`Parcel draw failed: plan host never sized (${cssW}x${cssH})`);
          return;
        }
        runDraw(cssW, cssH);
      });
    };

    // Double rAF after commit so flex/layout has settled; then retry if size still 0.
    rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      rafId = requestAnimationFrame(() => {
        if (cancelled) return;
        scheduleDrawAttempt(0);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [
    parcelData,
    parcelBounds,
    siteWidth,
    siteLength,
    parcelRenderTick,
    buildingFootprints,
    showBuildingFootprints,
    planViewZoom,
  ]);

  // Ctrl/Cmd + wheel on plan scroll area: view zoom (does not change stored feet).
  useEffect(() => {
    const visible =
      siteCreated ||
      (parcelData &&
        parcelBounds &&
        (Number(siteWidth) || 0) > 0 &&
        (Number(siteLength) || 0) > 0);
    if (!visible) return undefined;

    let attachedEl = null;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const dir = e.deltaY > 0 ? -1 : 1;
      setPlanViewZoom((prev) => clampPlanZoom(prev + dir * PLAN_ZOOM_STEP));
    };

    const t = window.setTimeout(() => {
      attachedEl = planOuterRef.current;
      if (attachedEl) attachedEl.addEventListener('wheel', onWheel, { passive: false });
    }, 0);

    return () => {
      clearTimeout(t);
      if (attachedEl) attachedEl.removeEventListener('wheel', onWheel);
    };
  }, [siteCreated, parcelData, parcelBounds, siteWidth, siteLength]);

  // Compute north arrow angle (screen rotation) when parcel bounds are available
  useEffect(() => {
    if (!parcelBounds) return;
    try {
      const centerLon = (parcelBounds.minLon + parcelBounds.maxLon) / 2;
      const centerLat = (parcelBounds.minLat + parcelBounds.maxLat) / 2;
      const northLat = centerLat + 0.0005;
      const project = (lon, lat, width, height) => ({
        x: ((lon - parcelBounds.minLon) / (parcelBounds.maxLon - parcelBounds.minLon)) * width,
        // invert y so increasing latitude points up on screen
        y: ((parcelBounds.maxLat - lat) / (parcelBounds.maxLat - parcelBounds.minLat)) * height
      });
      // Use current canvas size if available for consistent angle
      let width = 100, height = 100;
      if (canvasRef.current) {
        const r = canvasRef.current.getBoundingClientRect();
        width = Math.max(1, r.width);
        height = Math.max(1, r.height);
      }
      const p0 = project(centerLon, centerLat, width, height);
      const p1 = project(centerLon, northLat, width, height);
      const dx = p1.x - p0.x;
      const dy = p1.y - p0.y;
      const thetaRad = Math.atan2(dy, dx);
      const thetaDeg = (thetaRad * 180) / Math.PI;
      setNorthAngleDeg(thetaDeg);
    } catch (e) {
      addDebugMessage(`North angle compute failed: ${e.message}`);
    }
  }, [parcelBounds, canvasRef]);

  // Initialize/Update Leaflet basemap when enabled
  useEffect(() => {
    if (!parcelBounds || basemapMode !== 'leaflet') return;
    const container = leafletDivRef.current;
    if (!container) return;

    // Compute buffered bounds
    const dx = (parcelBounds.maxLon - parcelBounds.minLon) * basemapBufferPct;
    const dy = (parcelBounds.maxLat - parcelBounds.minLat) * basemapBufferPct;
    const minLon = parcelBounds.minLon - dx;
    const minLat = parcelBounds.minLat - dy;
    const maxLon = parcelBounds.maxLon + dx;
    const maxLat = parcelBounds.maxLat + dy;
    const bounds = L.latLngBounds(L.latLng(minLat, minLon), L.latLng(maxLat, maxLon));

    // Create or reuse map
    if (!leafletMapRef.current) {
      const map = L.map(container, {
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        tap: false
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 22
      }).addTo(map);
      leafletMapRef.current = map;
    }
    const map = leafletMapRef.current;
    // Fit to bounds precisely (no padding) and ensure size
    map.invalidateSize();
    map.fitBounds(bounds, { padding: [0, 0] });

    return () => {
      // Keep map for reuse; do not remove here to avoid flicker
    };
  }, [basemapMode, parcelBounds, basemapBufferPct]);

  // Calculate roof area from dimensions
  useEffect(() => {
    const length = parseFloat(roofLengthFt) || 0;
    const width = parseFloat(roofWidthFt) || 0;
    const area = length * width;
    setRoofAreaSqFt(area);
    addDebugMessage(`Roof area calculated: ${length} ft. × ${width} ft. = ${area} sq ft.`);
  }, [roofLengthFt, roofWidthFt]);

  // Update rain garden size when roof area or soil type changes (if site is created)
  useEffect(() => {
    addDebugMessage(`useEffect triggered: siteCreated=${siteCreated}, roofAreaSqFt=${roofAreaSqFt}, soilType=${soilType}`);
    if (siteCreated && roofAreaSqFt > 0 && soilType) {
      const minArea = computeMinRainGardenArea(roofAreaSqFt, targetRainfallIn, pondingDepthIn, soilType);
      addDebugMessage(`Updating rain garden size: roofArea=${roofAreaSqFt}, soilType=${soilType}, minArea=${minArea}`);
      setMinRainGardenAreaSqFt(minArea);
      // Only resize an existing rain garden; don't auto-create it (e.g., during Start Over).
      if (modules.some(m => m.type === 'rain-garden')) {
        ensureRainGardenMinSize(minArea);
      }
    } else {
      addDebugMessage(`useEffect skipped: siteCreated=${siteCreated}, roofAreaSqFt=${roofAreaSqFt}, soilType=${soilType}`);
    }
  }, [roofAreaSqFt, soilType, targetRainfallIn, pondingDepthIn, siteCreated, computeMinRainGardenArea, ensureRainGardenMinSize]);

  // Auto-create roof area structure when roof area is calculated
  useEffect(() => {
    if (!roofAreaSqFt || roofAreaSqFt <= 0) {
      // Remove roof area structure if no roof area
      setExistingStructures([]);
      return;
    }

    const roofLength = parseFloat(roofLengthFt) || 0;
    const roofWidth = parseFloat(roofWidthFt) || 0;
    
    // Create or update roof area structure
    const roofStructure = {
      id: 'roof-area',
      name: 'Roof Area',
      xPercent: 10, // Position in top-left area
      yPercent: 10,
      widthFt: roofWidth,
      heightFt: roofLength,
      rotation: 0,
      color: '#6b7280'
    };
    
    setExistingStructures([roofStructure]);
  }, [roofAreaSqFt, roofLengthFt, roofWidthFt]);

  const addModule = (type, quantity = 1, pickerColor) => {
    const moduleType = moduleTypes.find(m => m.id === type);
    if (!moduleType) return;
    if (moduleType.id === 'rain-garden') {
      const existing = modules.find(m => m.type === 'rain-garden');
      if (existing) {
        setSelectedModule(existing.id);
        return;
      }
      if (roofAreaSqFt > 0 && soilType) {
        const minA =
          minRainGardenAreaSqFt != null && minRainGardenAreaSqFt > 0
            ? minRainGardenAreaSqFt
            : computeMinRainGardenArea(roofAreaSqFt, targetRainfallIn, pondingDepthIn, soilType);
        const rgPal =
          pickerColor ||
          goalPickerSwatchColor(
            projectGoals.includes('stormwater-management')
              ? 'stormwater-management'
              : 'habitat-beautification',
            0,
            1
          );
        ensureRainGardenMinSize(minA, rgPal);
        return;
      }
    }

    const frontageFt = Math.max(0, Number(siteWidth) || 0);
    const depthFt = Math.max(0, Number(siteLength) || 0);
    const count = moduleType.quantitySelector
      ? Math.max(1, Math.min(50, Math.floor(Number(quantity)) || 1))
      : 1;

    let widthFt;
    let heightFt;
    if (moduleType.fixedFootprint) {
      widthFt = Math.min(
        moduleType.widthFt,
        frontageFt > 0 ? frontageFt : moduleType.widthFt
      );
      heightFt = Math.min(
        moduleType.heightFt,
        depthFt > 0 ? depthFt : moduleType.heightFt
      );
    } else if (moduleType.id === 'rain-garden') {
      widthFt = moduleType.widthFt;
      heightFt = moduleType.heightFt;
    } else {
      widthFt = frontageFt > 0 ? Math.min(moduleType.widthFt, frontageFt) : moduleType.widthFt;
      heightFt = moduleType.heightFt;
    }

    const newList = [];
    for (let i = 0; i < count; i++) {
      const col = i % 5;
      const row = Math.floor(i / 5);
      let xP = 10 + col * 3;
      let yP = 10 + row * 3;
      if (SETBACK_ENCLOSED_MODULE_IDS.has(moduleType.id)) {
        const c = clampModuleToFullSetbackRect(widthFt, heightFt, xP, yP, siteWidth, siteLength);
        xP = c.xPercent;
        yP = c.yPercent;
      }
      newList.push({
        id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 11)}`,
        type: moduleType.id,
        name: moduleType.name,
        color: pickerColor != null && pickerColor !== '' ? pickerColor : moduleType.color,
        widthFt,
        heightFt,
        xPercent: xP,
        yPercent: yP,
        rotation: 0,
        benefits: moduleType.benefits,
        depthIn: moduleType.depthIn || 0,
        soilMixDepthIn: moduleType.soilMixDepthIn || 0,
        baseDepthIn: moduleType.baseDepthIn || 0,
        reference: moduleType.reference,
        fixedFootprint: !!moduleType.fixedFootprint,
      });
    }
    setModules((prev) => [...prev, ...newList]);
    setSelectedModule(newList[newList.length - 1].id);
  };

  const rotateModule = (id) => {
    // Check if it's an existing structure
    const structure = existingStructures.find(s => s.id === id);
    if (structure) {
      setExistingStructures(existingStructures.map(s => {
        if (s.id === id) {
          const newRotation = (s.rotation + 90) % 360;
          return {
            ...s,
            rotation: newRotation,
            // Only swap width/height on 90° and 270° rotations
            widthFt: (newRotation === 90 || newRotation === 270) ? s.heightFt : s.widthFt,
            heightFt: (newRotation === 90 || newRotation === 270) ? s.widthFt : s.heightFt
          };
        }
        return s;
      }));
    } else {
      // Regular module
    setModules(modules.map(m => {
      if (m.id === id) {
        const newRotation = (m.rotation + 90) % 360;
        const nextWidthFt =
          (newRotation === 90 || newRotation === 270) ? m.heightFt : m.widthFt;
        const nextHeightFt =
          (newRotation === 90 || newRotation === 270) ? m.widthFt : m.heightFt;

        const wFtPlan = Math.max(1, Number(siteWidth) || 1);
        const lFtPlan = Math.max(1, Number(siteLength) || 1);
        const insetPctW = Math.min((SETBACK_FEET / wFtPlan) * 100, 49);
        const insetPctL = Math.min((SETBACK_FEET / lFtPlan) * 100, 49);
        const nextWidthPercent = (nextWidthFt / wFtPlan) * 100;
        const nextHeightPercent = (nextHeightFt / lFtPlan) * 100;

        let minX = 0;
        let maxX = 100 - nextWidthPercent;
        let minY = 0;
        let maxY = 100 - nextHeightPercent;

        if (SETBACK_ENCLOSED_MODULE_IDS.has(m.type)) {
          minX = insetPctW;
          maxX = 100 - insetPctW - nextWidthPercent;
          minY = insetPctL;
          maxY = 100 - insetPctL - nextHeightPercent;
        } else if (m.type === 'hoop-house') {
          minY = insetPctL;
        }

        const clampedX = Math.max(minX, Math.min(maxX, m.xPercent));
        const clampedY = Math.max(minY, Math.min(maxY, m.yPercent));

        return {
          ...m,
          rotation: newRotation,
          // Only swap width/height on 90° and 270° rotations
          widthFt: nextWidthFt,
          heightFt: nextHeightFt,
          xPercent: clampedX,
          yPercent: clampedY
        };
      }
      return m;
    }));
    }
  };

  const handleRotateMouseDown = (e, item, kind = 'module') => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedModule(item.id);
    // Don’t fight drag/resize while rotating.
    setDraggedModule(null);
    setResizeHandle(null);
    
    const host = planInnerRef.current || canvasRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const siteW = Math.max(1, Number(siteWidth) || 1);
    const siteL = Math.max(1, Number(siteLength) || 1);
    
    const wPercent = (item.widthFt / siteW) * 100;
    const hPercent = (item.heightFt / siteL) * 100;
    const cx = rect.left + rect.width * (item.xPercent + wPercent / 2) / 100;
    const cy = rect.top + rect.height * (item.yPercent + hPercent / 2) / 100;
    
    const pointerAngleDeg = (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
    setRotateHandle({
      kind,
      id: item.id,
      startRotationDeg: item.rotation || 0,
      startPointerAngleDeg: pointerAngleDeg,
    });
  };

  const handleModuleMouseDown = (e, module) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedModule(module.id);
    
    const host = planInnerRef.current || canvasRef.current;
    if (host) {
      const rect = host.getBoundingClientRect();
      const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
      const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
      
      setDraggedModule({
        id: module.id,
        offsetXPercent: xPercent - module.xPercent,
        offsetYPercent: yPercent - module.yPercent
      });
    }
  };

  const handleResizeMouseDown = (e, module, handle) => {
    e.preventDefault();
    e.stopPropagation();
    if (module.fixedFootprint) return;
    setSelectedModule(module.id);
    
    const host = planInnerRef.current || canvasRef.current;
    if (host) {
      const rect = host.getBoundingClientRect();
      setResizeHandle({
        id: module.id,
        handle: handle,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: module.widthFt,
        startHeight: module.heightFt,
        startXPercent: module.xPercent,
        startYPercent: module.yPercent,
        canvasWidth: rect.width,
        canvasHeight: rect.height
      });
      // allow new warning during this resize interaction
      minSizeWarnedRef.current = false;
    }
  };

  const handleMouseMove = (e) => {
    const snapThreshold = 2;
    
    if (rotateHandle && (planInnerRef.current || canvasRef.current)) {
      // Throttle rotate with rAF (keeps rotation smooth while dragging).
      pendingDragRef.current = { e: { clientX: e.clientX, clientY: e.clientY }, kind: 'rotate' };
      if (dragRafRef.current) return;
      dragRafRef.current = requestAnimationFrame(() => {
        const evt = pendingDragRef.current?.e;
        dragRafRef.current = null;
        if (!evt || !rotateHandle) return;
        
        const host = planInnerRef.current || canvasRef.current;
        if (!host) return;
        const rect = host.getBoundingClientRect();
        const mouseX = evt.clientX;
        const mouseY = evt.clientY;
        
        const siteW = Math.max(1, Number(siteWidth) || 1);
        const siteL = Math.max(1, Number(siteLength) || 1);

        startTransition(() => {
          if (rotateHandle.kind === 'structure') {
            setExistingStructures(prev =>
              prev.map(s => {
                if (s.id !== rotateHandle.id) return s;
                const wPercent = (s.widthFt / siteW) * 100;
                const hPercent = (s.heightFt / siteL) * 100;
                const cx = rect.left + rect.width * (s.xPercent + wPercent / 2) / 100;
                const cy = rect.top + rect.height * (s.yPercent + hPercent / 2) / 100;
                const pointerAngleDeg = (Math.atan2(mouseY - cy, mouseX - cx) * 180) / Math.PI;
                const newRotation = rotateHandle.startRotationDeg + (pointerAngleDeg - rotateHandle.startPointerAngleDeg);
                return { ...s, rotation: newRotation };
              })
            );
            return;
          }

          setModules(prev =>
            prev.map(m => {
              if (m.id !== rotateHandle.id) return m;
              const wPercent = (m.widthFt / siteW) * 100;
              const hPercent = (m.heightFt / siteL) * 100;
              const cx = rect.left + rect.width * (m.xPercent + wPercent / 2) / 100;
              const cy = rect.top + rect.height * (m.yPercent + hPercent / 2) / 100;
              const pointerAngleDeg = (Math.atan2(mouseY - cy, mouseX - cx) * 180) / Math.PI;
              const newRotation = rotateHandle.startRotationDeg + (pointerAngleDeg - rotateHandle.startPointerAngleDeg);
              return { ...m, rotation: newRotation };
            })
          );
        });
      });
      return;
    }
    
    if (resizeHandle && canvasRef.current) {
      // Throttle resize with rAF
      pendingDragRef.current = { e: { clientX: e.clientX, clientY: e.clientY }, kind: 'resize' };
      if (dragRafRef.current) return;
      dragRafRef.current = requestAnimationFrame(() => {
        const evt = pendingDragRef.current?.e;
        dragRafRef.current = null;
        if (!evt) return;
        const e = evt;
      const deltaX = e.clientX - resizeHandle.startX;
      const deltaY = e.clientY - resizeHandle.startY;
      const deltaWidthFt = (deltaX / resizeHandle.canvasWidth) * (Number(siteWidth) || 1);
      const deltaHeightFt = (deltaY / resizeHandle.canvasHeight) * (Number(siteLength) || 1);

      startTransition(() => setModules(modules.map(m => {
        if (m.id === resizeHandle.id) {
          if (m.fixedFootprint) {
            setTimeout(() => setResizeHandle(null), 0);
            return m;
          }
          let newWidth = m.widthFt;
          let newHeight = m.heightFt;
          let newX = m.xPercent;
          let newY = m.yPercent;

          switch(resizeHandle.handle) {
            case 'se':
              newWidth = Math.max(5, resizeHandle.startWidth + deltaWidthFt);
              newHeight = Math.max(5, resizeHandle.startHeight + deltaHeightFt);
              break;
            case 'sw':
              newWidth = Math.max(5, resizeHandle.startWidth - deltaWidthFt);
              newHeight = Math.max(5, resizeHandle.startHeight + deltaHeightFt);
              newX = resizeHandle.startXPercent + ((resizeHandle.startWidth - newWidth) / (Number(siteWidth) || 1)) * 100;
              break;
            case 'ne':
              newWidth = Math.max(5, resizeHandle.startWidth + deltaWidthFt);
              newHeight = Math.max(5, resizeHandle.startHeight - deltaHeightFt);
              newY = resizeHandle.startYPercent + ((resizeHandle.startHeight - newHeight) / (Number(siteLength) || 1)) * 100;
              break;
            case 'nw':
              newWidth = Math.max(5, resizeHandle.startWidth - deltaWidthFt);
              newHeight = Math.max(5, resizeHandle.startHeight - deltaHeightFt);
              newX = resizeHandle.startXPercent + ((resizeHandle.startWidth - newWidth) / (Number(siteWidth) || 1)) * 100;
              newY = resizeHandle.startYPercent + ((resizeHandle.startHeight - newHeight) / (Number(siteLength) || 1)) * 100;
              break;
            case 'e':
              newWidth = Math.max(5, resizeHandle.startWidth + deltaWidthFt);
              break;
            case 'w':
              newWidth = Math.max(5, resizeHandle.startWidth - deltaWidthFt);
              newX = resizeHandle.startXPercent + ((resizeHandle.startWidth - newWidth) / (Number(siteWidth) || 1)) * 100;
              break;
            case 's':
              newHeight = Math.max(5, resizeHandle.startHeight + deltaHeightFt);
              break;
            case 'n':
              newHeight = Math.max(5, resizeHandle.startHeight - deltaHeightFt);
              newY = resizeHandle.startYPercent + ((resizeHandle.startHeight - newHeight) / (Number(siteLength) || 1)) * 100;
              break;
            default:
              break;
          }

          const widthPercent = (newWidth / (Number(siteWidth) || 1)) * 100;
          const heightPercent = (newHeight / (Number(siteLength) || 1)) * 100;
          
          // Enforce minimum rain garden area if configured
          if (m.type === 'rain-garden' && minRainGardenAreaSqFt) {
            const proposedArea = newWidth * newHeight;
            if (proposedArea < minRainGardenAreaSqFt) {
              if (!minSizeWarnedRef.current) {
                minSizeWarnedRef.current = true;
                alert('This is the minimum size required for your site. To make the rain garden smaller, you must change the roof square footage.');
              }
              // stop resizing immediately to avoid repeated alerts and drift
              setTimeout(() => setResizeHandle(null), 0);
              return m; // keep previous size/position
            }
          }
          
          if (newX < snapThreshold) newX = 0;
          if (newY < snapThreshold) newY = 0;
          if (newX + widthPercent > 100 - snapThreshold) newX = 100 - widthPercent;
          if (newY + heightPercent > 100 - snapThreshold) newY = 100 - heightPercent;
          
          newX = Math.max(0, Math.min(100 - widthPercent, newX));
          newY = Math.max(0, Math.min(100 - heightPercent, newY));

          return {
            ...m,
            widthFt: newWidth,
            heightFt: newHeight,
            xPercent: newX,
            yPercent: newY
          };
        }
        return m;
      })));
      });
    } else if (draggedModule && (planInnerRef.current || canvasRef.current)) {
      // Throttle drag with rAF
      pendingDragRef.current = { e: { clientX: e.clientX, clientY: e.clientY }, kind: 'drag' };
      if (dragRafRef.current) return;
      dragRafRef.current = requestAnimationFrame(() => {
        const evt = pendingDragRef.current?.e;
        dragRafRef.current = null;
        if (!evt) return;
        const e = evt;
      const host = planInnerRef.current || canvasRef.current;
      const rect = host.getBoundingClientRect();
      const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
      const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
      
      // Check if it's an existing structure
      const structure = existingStructures.find(s => s.id === draggedModule.id);
      if (structure) {
        const widthPercent = (structure.widthFt / (Number(siteWidth) || 1)) * 100;
        const heightPercent = (structure.heightFt / (Number(siteLength) || 1)) * 100;
        
        let newX = xPercent - draggedModule.offsetXPercent;
        let newY = yPercent - draggedModule.offsetYPercent;
        
        if (newX < snapThreshold) newX = 0;
        if (newY < snapThreshold) newY = 0;
        if (newX + widthPercent > 100 - snapThreshold) newX = 100 - widthPercent;
        if (newY + heightPercent > 100 - snapThreshold) newY = 100 - heightPercent;
        
        startTransition(() => setExistingStructures(existingStructures.map(s => {
          if (s.id === draggedModule.id) {
            return {
              ...s,
              xPercent: Math.max(0, Math.min(100 - widthPercent, newX)),
              yPercent: Math.max(0, Math.min(100 - heightPercent, newY))
            };
          }
          return s;
        })));
      } else {
        // Regular module
      startTransition(() => setModules(modules.map(m => {
        if (m.id === draggedModule.id) {
          const widthPercent = (m.widthFt / (Number(siteWidth) || 1)) * 100;
          const heightPercent = (m.heightFt / (Number(siteLength) || 1)) * 100;
          
          let newX = xPercent - draggedModule.offsetXPercent;
          let newY = yPercent - draggedModule.offsetYPercent;
          
          if (newX < snapThreshold) newX = 0;
          if (newY < snapThreshold) newY = 0;
          if (newX + widthPercent > 100 - snapThreshold) newX = 100 - widthPercent;
          if (newY + heightPercent > 100 - snapThreshold) newY = 100 - heightPercent;

          // Hoop houses: allowed to touch frontage + depth lot lines, but cannot extend past the
          // rear-lot-line setback (top side of the plan).
          if (m.type === 'hoop-house') {
            const lFtPlan = Math.max(1, Number(siteLength) || 1);
            const insetPctL = Math.min((SETBACK_FEET / lFtPlan) * 100, 49);
            const minYWithRearSetback = Math.max(0, insetPctL);
            if (newY < minYWithRearSetback) newY = minYWithRearSetback;
          }

          if (SETBACK_ENCLOSED_MODULE_IDS.has(m.type)) {
            const c = clampModuleToFullSetbackRect(
              m.widthFt,
              m.heightFt,
              newX,
              newY,
              siteWidth,
              siteLength
            );
            newX = c.xPercent;
            newY = c.yPercent;
          }

          return {
            ...m,
            xPercent: Math.max(0, Math.min(100 - widthPercent, newX)),
            yPercent: Math.max(0, Math.min(100 - heightPercent, newY))
          };
        }
        return m;
      })));
      }
      });
    }
  };

  const handleMouseUp = () => {
    setDraggedModule(null);
    setResizeHandle(null);
    setRotateHandle(null);
  };

  // Attach global listeners during drag/resize so interactions work over UI layers
  useEffect(() => {
    if (!draggedModule && !resizeHandle && !rotateHandle) return;
    const onMove = (e) => handleMouseMove(e);
    const onUp = () => handleMouseUp();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [draggedModule, resizeHandle, rotateHandle]);

  const deleteModule = (id) => {
    setModules(modules.filter(m => m.id !== id));
    setSelectedModule(null);
  };

  const calculateMaterials = () => {
    const modulesMaterials = [];

    modules.filter(module => !module.isRoofArea).forEach(module => {
      const area = module.widthFt * module.heightFt;
      const volumeCuYd = (depth) => (area * (depth / 12)) / 27;
      const materials = {};
      
      switch(module.type) {
        case 'rain-garden':
          materials['Estimated number of herbaceous plugs for rain garden'] = Math.ceil(
            (area / plugSpacing) * MATERIAL_WASTE_FACTOR
          );
          materials['Bioretention Soil (cu yd)'] = Math.ceil(volumeCuYd(30) * MATERIAL_WASTE_FACTOR);
          materials['Mulch (cu yd)'] = Math.ceil(volumeCuYd(3) * MATERIAL_WASTE_FACTOR);
          materials['River Rock (tons)'] = Math.ceil((area / 200) * MATERIAL_WASTE_FACTOR);
          materials['Geotextile (sq yd)'] = Math.ceil((area / 9) * MATERIAL_WASTE_FACTOR);
          break;
          
        case 'bioswale':
          materials['Engineered Soil (cu yd)'] = Math.ceil(volumeCuYd(18) * MATERIAL_WASTE_FACTOR);
          materials['Native Grass Plugs'] = Math.ceil((area / (plugSpacing * 3)) * MATERIAL_WASTE_FACTOR);
          break;
          
        case 'permeable-paving':
          materials['Permeable Pavers (sq ft.)'] = Math.ceil(area * MATERIAL_WASTE_FACTOR);
          materials['Gravel Base (tons)'] = Math.ceil((volumeCuYd(12) * 1.4) * MATERIAL_WASTE_FACTOR);
          materials['Geotextile (sq yd)'] = Math.ceil((area / 9) * MATERIAL_WASTE_FACTOR);
          materials['Edge Restraint (ft.)'] = Math.ceil(((module.widthFt + module.heightFt) * 2) * MATERIAL_WASTE_FACTOR);
          break;
          
        case 'tree-planting':
          const groveTrees = Math.ceil(area / 225);
          materials['Trees (5-gal)'] = Math.ceil(groveTrees * MATERIAL_WASTE_FACTOR);
          materials['Stakes & Ties'] = groveTrees;
          break;

        case 'fruit-tree':
          const fruitTrees = Math.ceil(area / 225);
          materials['Fruit trees (5-gal)'] = Math.ceil(fruitTrees * MATERIAL_WASTE_FACTOR);
          materials['Stakes & Ties'] = fruitTrees;
          break;

        case 'shade-tree':
          const shadeTrees = Math.ceil(area / 225);
          materials['Shade trees (5-gal)'] = Math.ceil(shadeTrees * MATERIAL_WASTE_FACTOR);
          materials['Stakes & Ties'] = shadeTrees;
          break;

        case 'raised-bed':
          materials['Raised bed soil mix (cu yd)'] = Math.ceil(volumeCuYd(12) * MATERIAL_WASTE_FACTOR);
          materials['Frame lumber (linear ft est.)'] = Math.ceil(
            (module.widthFt + module.heightFt) * 2 * MATERIAL_WASTE_FACTOR
          );
          materials['Hardware / corner brackets (kit)'] = 1;
          break;

        case 'garden-row':
          materials['Topsoil / compost (cu yd)'] = Math.ceil(volumeCuYd(8) * MATERIAL_WASTE_FACTOR);
          materials['Mulch or straw (cu yd)'] = Math.ceil(volumeCuYd(2) * MATERIAL_WASTE_FACTOR);
          break;

        case 'hoop-house': {
          const longEdge = Math.max(module.widthFt, module.heightFt);
          materials['Poly cover / film (sq ft est.)'] = Math.ceil(area * 1.25 * MATERIAL_WASTE_FACTOR);
          materials['Hoop bows (~5 ft spacing along length)'] = Math.ceil(longEdge / 5);
          materials['Lumber baseboards (linear ft est.)'] = Math.ceil(
            (module.widthFt + module.heightFt) * 2 * MATERIAL_WASTE_FACTOR
          );
          materials['Anchors / ground stakes (kit)'] = 1;
          break;
        }

        case 'native-meadow':
          materials['Native Seed Mix (lbs)'] = Math.ceil((area / 1000) * MATERIAL_WASTE_FACTOR);
          materials['Erosion Control Blanket (sq yd)'] = Math.ceil((area / 9) * MATERIAL_WASTE_FACTOR);
          materials['Sand for drainage (cu yd) - if needed'] = Math.ceil(volumeCuYd(2) * MATERIAL_WASTE_FACTOR);
          break;
          
        case 'tallgrass-prairie':
          materials['Tallgrass Prairie Seed Mix (lbs)'] = Math.ceil((area / 1000) * MATERIAL_WASTE_FACTOR);
          materials['Erosion Control Blanket (sq yd)'] = Math.ceil((area / 9) * MATERIAL_WASTE_FACTOR);
          break;
          
        case 'cistern':
          materials['Cistern (1000 gal)'] = 1;
          materials['Downspout Kit'] = 1;
          materials['Filtration System'] = 1;
          break;
          
        case 'solar-array':
          materials['Solar Panels'] = Math.ceil((area / 20) * MATERIAL_WASTE_FACTOR);
          materials['Mounting Kits'] = Math.ceil(area / 200);
          break;
          
        case 'green-roof':
          materials['Growing Medium (cu yd)'] = Math.ceil(volumeCuYd(4) * MATERIAL_WASTE_FACTOR);
          materials['Drainage Mat (sq ft.)'] = Math.ceil(area * MATERIAL_WASTE_FACTOR);
          materials['Plants (trays)'] = Math.ceil((area / 50) * MATERIAL_WASTE_FACTOR);
          break;

        case 'rain-barrel':
          materials['Rain barrel (unit)'] = 1;
          materials['Downspout diverter kit'] = 1;
          materials['Overflow hose (ft.)'] = 10;
          break;

        case 'water-tank':
          materials['Water tank 250+ gal (unit)'] = 1;
          materials['Foundation pad (sq ft.)'] = Math.ceil(area * MATERIAL_WASTE_FACTOR);
          materials['Inlet / outlet fittings'] = 1;
          break;

        default:
          break;
      }
      
      modulesMaterials.push({
        id: module.id,
        name: module.name,
        dimensions: `${Math.round(module.widthFt)} ft. × ${Math.round(module.heightFt)} ft.`,
        area: Math.round(area),
        materials: materials
      });
    });
    
    return { modulesMaterials };
  };

  const calculateBenefits = () => {
    let stormwater = 0, co2 = 0;
    
    modules.forEach(m => {
      const area = m.widthFt * m.heightFt;
      const acres = area / 43560;
      
      switch(m.type) {
        case 'rain-garden':
          stormwater += area * 7.5;
          co2 += acres * 600;
          break;
        case 'bioswale':
          stormwater += area * 6;
          co2 += acres * 400;
          break;
        case 'tree-planting':
          const groveTreeCount = Math.ceil(area / 225);
          co2 += groveTreeCount * 48;
          stormwater += area * 3.6;
          break;
        case 'fruit-tree':
          const fruitTreeCount = Math.ceil(area / 225);
          co2 += fruitTreeCount * 48;
          stormwater += area * 3.6;
          break;
        case 'shade-tree':
          const shadeTreeCount = Math.ceil(area / 225);
          co2 += shadeTreeCount * 48;
          stormwater += area * 3.6;
          break;
        case 'raised-bed':
          co2 += (area / 43560) * 350;
          stormwater += area * 2.5;
          break;
        case 'garden-row':
          co2 += (area / 43560) * 500;
          stormwater += area * 3;
          break;
        case 'hoop-house':
          co2 += (area / 43560) * 600;
          stormwater += area * 1.5;
          break;
        case 'native-meadow':
          stormwater += area * 4.8;
          co2 += acres * 800;
          break;
        case 'tallgrass-prairie':
          stormwater += area * 5.2;
          co2 += acres * 2000;
          break;
        case 'permeable-paving':
          stormwater += area * 8.4;
          break;
        case 'green-roof':
          stormwater += area * 7.2;
          co2 += area * 0.2;
          break;
        case 'cistern':
          stormwater += 1000;
          break;
        case 'rain-barrel':
          stormwater += 50;
          break;
        case 'water-tank':
          stormwater += 250;
          break;
        default:
          break;
      }
    });
    
    return { stormwater, co2 };
  };

  const exportJSON = () => {
    const { modulesMaterials } = calculateMaterials();
    const benefits = calculateBenefits();
    
    const data = {
      project: {
        site: { width: siteWidth, length: siteLength, totalArea: siteWidth * siteLength },
        standards: ['DWSD Manual 2018', 'SEMCOG LID Manual', 'Detroit PCSWMO'],
        materialQuantityMultiplier: MATERIAL_WASTE_FACTOR,
        rainGardenHerbaceousPlugs: '1 plug per sq ft of rain garden footprint (estimated; scales with plan size)'
      },
      modulesMaterials: modulesMaterials,
      ecologicalBenefits: {
        stormwaterRetention: `${Math.round(benefits.stormwater).toLocaleString()} gal/yr`,
        co2Sequestration: `${Math.round(benefits.co2)} lbs/yr`
      },
      references: [
        'Rain garden CO2: Bioretention systems can mitigate ~70% of carbon footprint (Kavehei et al. 2018)',
        'Tallgrass prairie: 0.3-1.7 tons C/acre/year, avg 1 ton/acre/year (Tallgrass Ontario, U of MN)',
        'Plant density: 1 plug/sq ft. (Ann Arbor rain garden standards)'
      ]
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lid-site-plan-${Date.now()}.json`;
    a.click();
  };

  const exportPDF = async () => {
    const loadScriptOnce = (src, globalPath) => new Promise((resolve, reject) => {
      try {
        const get = () => {
          if (!globalPath) return null;
          return globalPath.split('.').reduce((acc, k) => (acc ? acc[k] : null), window);
        };
        if (get()) return resolve();
        const existing = Array.from(document.querySelectorAll('script')).find((s) => s.src === src);
        if (existing) {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', (e) => reject(e));
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = (e) => reject(e);
        document.head.appendChild(script);
      } catch (e) {
        reject(e);
      }
    });

    await loadScriptOnce(
      'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
      'jspdf.jsPDF'
    );
    await loadScriptOnce(
      'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js',
      'html2canvas'
    );

    const { jsPDF } = window.jspdf;
    const html2canvas = window.html2canvas;

    const wFt = Math.max(1, Number(siteWidth) || 1);
    const lFt = Math.max(1, Number(siteLength) || 1);
    const isLandscape = wFt > lFt * 1.02 ? true : lFt > wFt * 1.02 ? false : false; // square -> portrait

    const fmt = pdfPaperSize === 'tabloid' ? 'tabloid' : 'letter';
    const orientation = isLandscape ? 'landscape' : 'portrait';
    const pdf = new jsPDF({ orientation, unit: 'pt', format: fmt });

    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    // Margins: 0.165"
    const MARGIN_PT = 0.165 * 72;
    const innerX = MARGIN_PT;
    const innerY = MARGIN_PT;
    const innerW = pageW - 2 * MARGIN_PT;
    const innerH = pageH - 2 * MARGIN_PT;

    const printedScaleCandidates = [10, 15, 20, 25, 30, 40, 50]; // feet per inch
    const legendW = 150;
    const dimLabelPad = 22;
    // Room below plan for graphic scale (2 in. bar + ticks + foot labels + caption)
    const scaleBarH = 52;

    // Header: title + meta; plan top (lot line) below with room for "Rear Lot Line" label
    const META_START_Y = innerY + 38;
    const META_LINE_LEAD = 12;
    const metaLineCount = 5;
    const metaBlockBottom = META_START_Y + (metaLineCount - 1) * META_LINE_LEAD + 4;
    const planY = metaBlockBottom + 24;
    const headerTotalH = planY - innerY;

    const availablePlanW = innerW - legendW - dimLabelPad;
    const availablePlanH = innerH - headerTotalH - scaleBarH;

    let chosenFeetPerIn = printedScaleCandidates[printedScaleCandidates.length - 1];
    for (let i = 0; i < printedScaleCandidates.length; i++) {
      const ftPerIn = printedScaleCandidates[i];
      const planWpt = (wFt / ftPerIn) * 72;
      const planHpt = (lFt / ftPerIn) * 72;
      if (planWpt <= availablePlanW && planHpt <= availablePlanH) {
        chosenFeetPerIn = ftPerIn;
        break;
      }
    }

    const planTargetWpt = (wFt / chosenFeetPerIn) * 72;
    const planTargetHpt = (lFt / chosenFeetPerIn) * 72;

    // PAGE 1: Site plan page — single title: "Site Plan: <street>"
    const streetLineForPdf = sitePlanStreetLineOnly(address, parcelData, parcelAddressSnapshot);
    const pdfMainTitle = streetLineForPdf ? `Site Plan: ${streetLineForPdf}` : 'Site Plan';
    const printedScaleText = `1 inch = ${chosenFeetPerIn} feet`;
    const totalSqFt = (wFt * lFt).toLocaleString();
    const dateText = new Date().toLocaleDateString();

    pdf.setTextColor('#111827');
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    pdf.text(pdfMainTitle, innerX, innerY + 20);
    pdf.setFont(undefined, 'normal');

    pdf.setFontSize(10);
    const metaLines = [
      `Site Dimensions: ${wFt} ft. × ${lFt} ft.`,
      `Area: ${totalSqFt} sq ft.`,
      `Base Scale: 1 ft = ${PLAN_PX_PER_FOOT} pixels`,
      `Printed Scale: ${printedScaleText}`,
      `Date: ${dateText}`,
    ];
    metaLines.forEach((t, idx) => pdf.text(t, innerX, META_START_Y + idx * META_LINE_LEAD));

    // Legend (left): top of "Legend" aligns with top of plan / rear lot line (planY)
    const legendX = innerX;
    const legendTitleBaseline = planY + 7; // ~cap-height so visual top meets planY
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.text('Legend', legendX, legendTitleBaseline);
    pdf.setFont(undefined, 'normal');

    const lineY1 = legendTitleBaseline + 16;
    pdf.setDrawColor('#1f2937');
    pdf.setLineWidth(2);
    pdf.setLineDashPattern(PLAN_BOUNDARY_DASH, 0);
    pdf.line(legendX, lineY1, legendX + 56, lineY1);
    pdf.setLineDashPattern([], 0);
    pdf.text('Lot line', legendX + 64, lineY1 + 3);

    const lineY2 = legendTitleBaseline + 34;
    pdf.setDrawColor(SETBACK_LINE_COLOR);
    pdf.setLineWidth(2);
    // dashed (approx)
    pdf.setLineDashPattern([8, 6], 0);
    pdf.line(legendX, lineY2, legendX + 56, lineY2);
    pdf.setLineDashPattern([], 0);
    pdf.text('5 ft setback', legendX + 64, lineY2 + 3);

    // Plan image placement
    const planX = innerX + legendW;

    // Capture plan via html2canvas on canvasRef.current (full plan container)
    if (!canvasRef.current) {
      alert('Plan is not ready to export yet.');
      return;
    }
    const capCanvas = await html2canvas(canvasRef.current, {
      backgroundColor: '#f0fdf4',
      scale: 2,
      useCORS: true,
      onclone: (clonedDoc) => {
        lidApplyPdfLabelLayerSplitInClone(clonedDoc);
      },
    });
    const imgData = capCanvas.toDataURL('image/png');

    // Plan image first so rotated depth text (extends left of its anchor) is not covered by the lot fill
    pdf.addImage(imgData, 'PNG', planX, planY, planTargetWpt, planTargetHpt);

    // Dimension labels (match on-screen plan copy; draw after image)
    pdf.setFontSize(10);
    pdf.setTextColor('#111827');
    pdf.text(`REAR LOT LINE ${wFt} ft.`, planX + planTargetWpt / 2, planY - 10, { align: 'center' });
    pdf.text(`FRONTAGE ${wFt} ft.`, planX + planTargetWpt / 2, planY + planTargetHpt + 18, { align: 'center' });
    // PDF-only depth label: offset right of lot; angle 90° (= prior −90° plus 180° CCW)
    const depthLabel = `DEPTH ${lFt} ft.`;
    const depthLabelRightPadPt = 48;
    const depthLabelX = planX + planTargetWpt + depthLabelRightPadPt;
    pdf.text(depthLabel, depthLabelX, planY + planTargetHpt / 2, {
      angle: 90,
      align: 'center',
    });

    // Graphic scale: exactly 2 in. on paper; each 0.5 in. = chosenFeetPerIn/2 ft (matches plan scale)
    const PT_PER_IN = 72;
    const sbBarWpt = 2 * PT_PER_IN;
    const sbHalfInPt = PT_PER_IN / 2;
    const sbX = Math.max(innerX, planX + planTargetWpt - sbBarWpt);
    const sbY = planY + planTargetHpt + 28;
    const tickH = 9;
    const maxFtOnBar = 2 * chosenFeetPerIn;

    pdf.setDrawColor('#111827');
    pdf.setLineDashPattern([], 0);
    pdf.setLineWidth(1);
    pdf.line(sbX, sbY, sbX + sbBarWpt, sbY);
    pdf.setLineWidth(0.75);
    for (let i = 0; i <= 4; i += 1) {
      const tx = sbX + i * sbHalfInPt;
      pdf.line(tx, sbY, tx, sbY - tickH);
    }

    pdf.setFontSize(8);
    pdf.setTextColor('#111827');
    for (let i = 0; i <= 4; i += 1) {
      const tx = sbX + i * sbHalfInPt;
      const ftVal = (i * chosenFeetPerIn) / 2;
      const lbl =
        i === 0
          ? '0'
          : Math.abs(ftVal - Math.round(ftVal)) < 0.05
            ? `${Math.round(ftVal)}'`
            : `${ftVal.toFixed(1)}'`;
      pdf.text(lbl, tx, sbY + 11, { align: 'center' });
    }
    pdf.setFontSize(8);
    pdf.text(
      `Scale on paper: 1 in. = ${chosenFeetPerIn} ft — bar length 2.0 in. = ${maxFtOnBar} ft on plan`,
      sbX + sbBarWpt,
      sbY + 22,
      { align: 'right' }
    );

    // PAGE 2: Materials List (improved spacing + up to 3 columns, no module split across columns)
    pdf.addPage();
    pdf.setTextColor('#111827');
    pdf.setFontSize(14);
    pdf.text('Materials List', innerX, innerY + 18);

    const { modulesMaterials } = calculateMaterials();
    const colCount = 3;
    const colGap = 16;
    const colW = (innerW - colGap * (colCount - 1)) / colCount;
    const topY = innerY + 40;
    const bottomY = innerY + innerH;
    const lineH = 12;

    let col = 0;
    let cursorY = topY;
    const colX = (c) => innerX + c * (colW + colGap);

    const measureBlockLines = (mod) => {
      const lines = [];
      lines.push(`${mod.name} (${mod.dimensions})`);
      Object.entries(mod.materials).forEach(([mat, qty]) => lines.push(`- ${mat}: ${qty}`));
      return lines;
    };

    const placeBlock = (lines) => {
      const blockH = lines.length * lineH + 8;
      if (cursorY + blockH > bottomY) {
        col += 1;
        cursorY = topY;
        if (col >= colCount) {
          pdf.addPage();
          pdf.setFontSize(14);
          pdf.text('Materials List (cont.)', innerX, innerY + 18);
          col = 0;
          cursorY = topY;
        }
      }
      const x = colX(col);
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'bold');
      pdf.text(lines[0], x, cursorY);
      pdf.setFont(undefined, 'normal');
      cursorY += lineH;
      pdf.setFontSize(9);
      for (let i = 1; i < lines.length; i++) {
        pdf.text(lines[i], x, cursorY);
        cursorY += lineH;
      }
      cursorY += 8;
    };

    modulesMaterials.forEach((mod) => placeBlock(measureBlockLines(mod)));

    // PAGE 3: Eco Benefits
    pdf.addPage();
    pdf.setTextColor('#111827');
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'normal');
    pdf.text('Projected Annual Ecological Benefits', innerX, innerY + 18);
    const benefits = calculateBenefits();
    pdf.setFontSize(11);
    pdf.text(`Stormwater Retention: ${Math.round(benefits.stormwater).toLocaleString()} gal/yr`, innerX, innerY + 48);
    pdf.text(`CO2 Sequestration: ${Math.round(benefits.co2).toLocaleString()} lbs/yr`, innerX, innerY + 66);

    const presentModuleTypes = new Set(modules.map((m) => m.type));
    const pdfSources = [];
    const addPdfSource = (id, citation, url) => {
      if (pdfSources.some((s) => s.id === id)) return;
      pdfSources.push({ id, citation, url });
    };

    if (presentModuleTypes.has('rain-garden')) {
      addPdfSource(
        'kavehei-2018-rain-garden',
        'Kavehei, E., Jenkins, G., Adame, M., & Lemckert, C. (2018). Carbon sequestration potential for mitigating the carbon footprint of green stormwater infrastructure. Renewable and Sustainable Energy Reviews, 94, 1179-1191.',
        'https://www.sciencedirect.com/science/article/abs/pii/S1364032118305057'
      );
    }
    if (presentModuleTypes.has('bioswale')) {
      addPdfSource(
        'kavehei-2018-bioswale',
        'Kavehei, E., Jenkins, G., Adame, M., & Lemckert, C. (2018). Carbon sequestration potential for mitigating the carbon footprint of green stormwater infrastructure. Renewable and Sustainable Energy Reviews, 94, 1179-1191. (Conservative estimate applied to bioswale as equivalent bioretention system.)',
        'https://www.sciencedirect.com/science/article/abs/pii/S1364032118305057'
      );
    }
    if (
      presentModuleTypes.has('tree-planting') ||
      presentModuleTypes.has('fruit-tree') ||
      presentModuleTypes.has('shade-tree')
    ) {
      addPdfSource(
        'unm-usda-tree-carbon',
        'University of New Mexico / USDA. Calculating tree carbon sequestration. Temperate climate average ~50 lbs CO2/tree/yr.',
        'https://www.unm.edu/~jbrink/365/Documents/Calculating_tree_carbon.pdf'
      );
    }
    if (presentModuleTypes.has('green-roof')) {
      addPdfSource(
        'getter-2009-green-roof',
        'Getter, K.L., Rowe, D.B., Robertson, G.P., Cregg, B.M., & Andresen, J.A. (2009). Carbon sequestration potential of extensive green roofs. Environmental Science & Technology, 43(19), 7564-7570. Study conducted on Michigan green roofs. Sequestration rate of 0.038 lbs CO2/sq ft/yr applied in this calculator.',
        'https://pubs.acs.org/doi/10.1021/es901539x'
      );
    }
    if (presentModuleTypes.has('native-meadow')) {
      addPdfSource(
        'garcia-2011-native-meadow',
        'Garcia Alvarez, M. (2011). Linking prairie carbon sequestration and other co-benefits. M.Sc. Thesis, U.S. Forest Service / Midewin National Tallgrass Prairie. Applied as conservative estimate for native meadow (800 lbs CO2/acre/yr) given higher forb ratio relative to tallgrass prairie.',
        'https://skemman.is/bitstream/1946/9755/1/Garcia_Alvarez_MA_Thesis.pdf'
      );
    }
    if (presentModuleTypes.has('tallgrass-prairie')) {
      addPdfSource(
        'garcia-2011-tallgrass',
        'Garcia Alvarez, M. (2011). Linking prairie carbon sequestration and other co-benefits. M.Sc. Thesis, U.S. Forest Service / Midewin National Tallgrass Prairie. Based on review of 29 studies; perennial grasslands average 1.7 metric tons CO2/acre/year (3,748 lbs). App uses 2,000 lbs/acre/yr as a rounded conservative midpoint.',
        'https://skemman.is/bitstream/1946/9755/1/Garcia_Alvarez_MA_Thesis.pdf'
      );
    }
    if (presentModuleTypes.has('rain-garden') || presentModuleTypes.has('bioswale')) {
      addPdfSource(
        'ann-arbor-semcog-plant-spacing',
        'Ann Arbor bioretention guidelines cross-referenced with SEMCOG Low Impact Development Manual. Native plant spacing standard: 1 plant per square foot for dense bioretention planting.',
        'https://www.semcog.org/low-impact-development'
      );
    }

    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text('Sources:', innerX, innerY + 96);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(9);
    let srcY = innerY + 112;
    const srcBottomY = innerY + innerH - 24;
    const ensureSourceSpace = (neededHeight = 0) => {
      if (srcY + neededHeight <= srcBottomY) return;
      pdf.addPage();
      pdf.setTextColor('#111827');
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Sources (cont.):', innerX, innerY + 18);
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      srcY = innerY + 34;
    };
    if (pdfSources.length === 0) {
      pdf.text('No module-specific citations for selected modules.', innerX, srcY);
    } else {
      pdfSources.forEach((src, idx) => {
        const citeLines = pdf.splitTextToSize(`${idx + 1}. ${src.citation}`, innerW);
        const urlLines = pdf.splitTextToSize(src.url, innerW);
        ensureSourceSpace((citeLines.length + urlLines.length) * 11 + 8);
        citeLines.forEach((line) => {
          pdf.text(line, innerX, srcY);
          srcY += 11;
        });
        urlLines.forEach((line) => {
          pdf.text(line, innerX, srcY);
          srcY += 11;
        });
        srcY += 8;
      });
    }

    pdf.save('lid-site-plan.pdf');
  };

  const { modulesMaterials } = calculateMaterials();
  const benefits = calculateBenefits();

  const showSitePlanPanel =
    siteCreated ||
    (parcelData &&
      parcelBounds &&
      (Number(siteWidth) || 0) > 0 &&
      (Number(siteLength) || 0) > 0);

  const sitePlanStreetLine = sitePlanStreetLineOnly(address, parcelData, parcelAddressSnapshot);
  const sitePlanTitleText = sitePlanStreetLine ? `SITE PLAN: ${sitePlanStreetLine}` : 'SITE PLAN';

  return (
    <div className="w-full h-screen bg-gray-50 flex">
      <div
        ref={sidebarScrollRef}
        className="bg-white border-r border-gray-200 overflow-y-auto overflow-x-hidden shrink-0 min-w-0"
        style={{
          width: `${sidebarWidth}rem`,
          maxWidth: `${sidebarWidth}rem`,
          minWidth: 0,
        }}
      >
        <div className="lid-sidebar pt-12 pb-12 pl-12 pr-0.5">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">LID Site Planning Prototype</h1>
          
          {/* Debug panel + Back to Setup: stacked so buttons never share one line */}
          <div className="mb-[calc(1rem-1px)] flex flex-col items-center gap-4">
            {showDebugPanel && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg w-full">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <h3 className="text-base font-semibold text-yellow-800">Debug Panel</h3>
                    <p className="text-xs text-yellow-700">The content in this box is for developers and will not be present in the live version of this app. Please disregard during user testing.</p>
                  </div>
                  <button 
                    type="button"
                    onClick={() => setShowDebugPanel(false)}
                    className="lid-debug-toggle-button shrink-0 ml-3"
                  >
                    Hide
                  </button>
                </div>
                <div className="text-xs text-yellow-700 max-h-32 overflow-y-auto">
                  {debugMessages.length === 0 ? (
                    <div>No debug messages yet. Try entering a Detroit address and clicking Lookup.</div>
                  ) : (
                    debugMessages.map((msg, index) => (
                      <div key={index} className="mb-1">{msg}</div>
                    ))
                  )}
                </div>
              </div>
            )}
            {!showDebugPanel && (
              <button 
                type="button"
                onClick={() => setShowDebugPanel(true)}
                className="lid-debug-toggle-button self-start"
              >
                Show Debug Panel
              </button>
            )}
            {siteCreated && (
              <div className="flex w-full max-w-full flex-row items-center justify-between gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    clearPlannerSession();
                    setParcelAddressSnapshot('');
                    setSiteCreated(false);
                  }}
                  className="lid-panel-btn lid-back-setup-btn rounded-lg shrink-0"
                >
                  BACK
                </button>
                <button
                  type="button"
                  onClick={startOver}
                  className="lid-panel-btn lid-reset-modules-btn shrink-0 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium"
                >
                  Reset modules
                </button>
              </div>
            )}
          </div>

          {!siteCreated ? (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  SELECT Project Goals <span className="font-normal">(select all that apply)</span>
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'food-production', label: 'Food Production' },
                    { id: 'habitat-beautification', label: 'Habitat & Beautification' },
                    { id: 'stormwater-management', label: 'Stormwater Management' },
                    { id: 'waste-recycling', label: 'Waste & Recycling' },
                    { id: 'shared-spaces', label: 'Shared Spaces' },
                    { id: 'paths-edges', label: 'Paths & Edges' },
                    { id: 'clean-energy', label: 'Clean Energy' }
                  ].map(goal => (
                    <label key={goal.id} className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={projectGoals.includes(goal.id)}
                        onChange={() => toggleProjectGoal(goal.id)}
                        className="mr-2"
                      />
                      {goal.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  ENTER Site Address <span className="font-normal">(optional)</span>
                </h3>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => { setAddress(e.target.value); if (addressWarning) setAddressWarning(''); }}
                    onKeyDown={(e) => {
                      addDebugMessage(`Key pressed: ${e.key}`);
                      if (e.key === 'Enter' && address.trim() && !isLookingUpAddress) {
                        addDebugMessage(`Enter key pressed, calling lookupAddress for: "${address}"`);
                        lookupAddress();
                      } else if (e.key === 'Enter') {
                        addDebugMessage(`Enter key pressed but conditions not met: address="${address}", isLookingUpAddress=${isLookingUpAddress}`);
                      }
                    }}
                    placeholder="123 MAIN ST DETROIT MI 48201 (ZIP required)"
                    className="lid-site-address-input flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      addDebugMessage(`Lookup button clicked for address: "${address}"`);
                      lookupAddress();
                    }}
                    disabled={!address.trim() || isLookingUpAddress}
                    className="lid-lookup-btn bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isLookingUpAddress ? 'Looking up...' : 'Lookup'}
                  </button>
                </div>
                {addressWarning && (
                  <p className="text-sm text-red-600 mt-2">{addressWarning}</p>
                )}
                <p className="lid-usps-address-caption text-black font-medium mt-2">
                  USPS format: 123 MAIN ST DETROIT MI 48201 (no commas or periods)
                  <br />
                  Two-letter state code (MI) and a 5-digit ZIP are required.
                </p>
                {isLoadingTopography && (
                  <p className="text-xs text-gray-600 mt-1">
                    Loading topography data from USGS...
                  </p>
                )}
                {isLoadingParcelData && (
                  <p className="text-xs text-red-600 mt-1">
                    Loading parcel data from Detroit...
                  </p>
                )}
                {parcelData && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Parcel data loaded ({parcelData.features.length} parcels found)
                  </p>
                )}
                {ENABLE_BUILDING_FOOTPRINTS && isLoadingBuildings && (
                  <p className="text-xs text-gray-600 mt-1">
                    Loading building footprints (SEMCOG)…
                  </p>
                )}
                {/* Building-footprint toggle/status hidden for now until footprint loading is finalized. */}
                {/* Topography status hidden for now */}
              </div>

              

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">ENTER Site Dimensions</h3>
                <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Frontage (site width in feet)
                </label>
                <input
                  type="number"
                  value={siteWidth}
                  onChange={(e) => {
                    setSiteWidth(e.target.value);
                    addDebugMessage(`Width manually changed to: ${e.target.value}`);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder=""
                  title={parcelData ? 'Frontage and Depth populated from parcel data - you can still edit' : 'Enter site frontage'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Depth (site length in feet)
                </label>
                <input
                  type="number"
                  value={siteLength}
                  onChange={(e) => {
                    setSiteLength(e.target.value);
                    addDebugMessage(`Length manually changed to: ${e.target.value}`);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder=""
                  title={parcelData ? 'Frontage and Depth populated from parcel data - you can still edit' : 'Enter site depth'}
                />
              </div>
                </div>
                </div>

              {parcelData && (
                <div className="mb-4">
                  <p className="lid-calculated-roof-area-text font-semibold tracking-wide">
                    <span
                      style={{
                        fontFamily: 'HeaderFont',
                        color: '#000000',
                      }}
                    >
                      PARCEL-BASED SITE PLAN AREA:
                    </span>{' '}
                    <span style={{ color: '#000000' }}>
                      {((Number(siteWidth) || 0) * (Number(siteLength) || 0)).toLocaleString()} sq ft.
                    </span>
                  </p>
                </div>
              )}

              <div>
                <div className="space-y-4">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Site Type
                    </label>
                    <select
                      value={siteType}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSiteType(val);
                        const v = String(val).toLowerCase();
                        setHasRoof(!v.includes('vacant'));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">SELECT SITE TYPE...</option>
                      {(siteTypeOptions.length > 0 ? siteTypeOptions : (siteType ? [siteType] : []))
                        .map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Soil Type
                    </label>
                      <select
                        value={soilType}
                        onChange={(e) => setSoilType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">SELECT SOIL TYPE...</option>
                        <option value="urban-disturbed">URBAN/DISTURBED</option>
                        <option value="clay-loam">CLAY LOAM</option>
                        <option value="sandy-loam">SANDY LOAM</option>
                        <option value="loam">LOAM</option>
                        <option value="organic">ORGANIC (PEAT/MUCK)</option>
                        <option value="mixed">MIXED</option>
                      </select>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-800 mb-3">CALCULATE ROOF AREA FOR RAIN CATCHMENT</h3>

                  <div>
                    <label className="flex items-center text-sm mb-4">
                      <input
                        type="checkbox"
                        checked={hasRoof}
                        onChange={(e) => setHasRoof(e.target.checked)}
                        className="mr-2"
                      />
                      <span>Is there a roof area you will be using to capture rainwater?</span>
                    </label>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">ENTER ROOF DIMENSIONS</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Roof Width (ft.)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={roofWidthFt}
                          onChange={(e) => setRoofWidthFt(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="e.g., 30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Roof Length (ft.)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={roofLengthFt}
                          onChange={(e) => setRoofLengthFt(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="e.g., 40"
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="lid-calculated-roof-area-text font-semibold tracking-wide">
                        <span
                          style={{
                            fontFamily: 'HeaderFont',
                            color: '#000000',
                          }}
                        >
                          CALCULATED ROOF AREA:
                        </span>{' '}
                        <span style={{ color: '#000000' }}>
                          {roofAreaSqFt > 0 ? `${Math.round(roofAreaSqFt).toLocaleString()} sq ft.` : ''}
                        </span>
                      </p>
                      <p className="lid-roof-area-module-note mt-1" style={{ color: '#000000' }}>
                        * A roof area module will appear on the site plan for you to position
                      </p>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-3">Calculate Average Rainfall</h3>
                    <div className="grid grid-cols-2 gap-3 mt-0">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Average Rainfall (in)</label>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="3"
                          value={targetRainfallIn}
                          onChange={(e) => {
                            const raw = Number(e.target.value);
                            // Constrain to 0.5 inch increments for consistent sizing.
                            const roundedToHalf = Math.round(raw * 2) / 2;
                            const clamped = Math.max(0, Math.min(3, roundedToHalf));
                            setTargetRainfallIn(clamped);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Ponding Depth (in)</label>
                        <input
                          type="number"
                          step="1"
                          min="3"
                          value={pondingDepthIn}
                          onChange={(e) => setPondingDepthIn(Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                    {minRainGardenAreaSqFt && (
                      <p className="text-xs text-green-700 mt-2">Minimum rain garden area: {Math.round(minRainGardenAreaSqFt).toLocaleString()} sq ft. (sized for {targetRainfallIn}" storm)</p>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center text-sm mb-2">
                      <input
                        type="checkbox"
                        checked={hasUtilities}
                        onChange={(e) => setHasUtilities(e.target.checked)}
                        className="mr-2"
                      />
                      Has utilities access (for solar, cistern)
                    </label>
                  </div>

                  
                </div>
              </div>

              <div className="flex justify-center w-full">
                <button
                  type="button"
                  onClick={createSite}
                  className="lid-panel-btn bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  Launch Site Planner
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h3 className="text-3xl font-semibold text-gray-800 mb-2 uppercase">
                  Add Modules to the Site Plan
                </h3>
                <div className="space-y-3 mb-3 text-gray-700">
                  <p className="m-0">
                    The modules available based on your goal selections are below. Some modules depend on site
                    conditions such as soil type. If the module you want does not appear below, your site might not be
                    suitable for this module.
                  </p>
                </div>
                <div className="flex flex-col gap-6 w-full">
                  {(() => {
                    const filtered = getFilteredModules();
                    const sections = groupFilteredModulesByGoals(filtered, projectGoals);
                    const roofRgMinArea =
                      roofAreaSqFt > 0 && soilType
                        ? minRainGardenAreaSqFt != null && minRainGardenAreaSqFt > 0
                          ? minRainGardenAreaSqFt
                          : computeMinRainGardenArea(
                              roofAreaSqFt,
                              targetRainfallIn,
                              pondingDepthIn,
                              soilType
                            )
                        : null;
                    const showRoofRainGardenRow = roofRgMinArea != null && roofRgMinArea > 0;
                    const rgPlaced = modules.find((m) => m.type === 'rain-garden');
                    const roofRgSectionId = projectGoals.includes('stormwater-management')
                      ? 'stormwater-management'
                      : projectGoals.includes('habitat-beautification')
                        ? 'habitat-beautification'
                        : null;
                    const roofRgSectionInList =
                      roofRgSectionId != null && sections.some((s) => s.id === roofRgSectionId);

                    if (filtered.length === 0 && !showRoofRainGardenRow) {
                      return (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          <p>No modules match your current filters.</p>
                          <p className="text-xs mt-1">Try adjusting your site characteristics above.</p>
                        </div>
                      );
                    }
                    const renderTypeRow = (type, swatchColor) => {
                      const placedOfType = modules.filter((m) => m.type === type.id).length;
                      if (type.quantitySelector) {
                        const hasAnyPlaced = placedOfType > 0;
                        const isEditing = !!pickerQtyEditing[type.id];
                        const qtyBaseRaw = moduleQuantities[type.id] ?? 0;
                        const draftQtyRaw =
                          pickerQtyDraft[type.id] != null ? pickerQtyDraft[type.id] : qtyBaseRaw;
                        const qtyForAddRaw = isEditing ? draftQtyRaw : qtyBaseRaw;
                        const qtyForAdd = Math.max(0, Math.min(50, parseInt(qtyForAddRaw, 10) || 0));
                        const qtyIsValid = qtyForAdd > 0;
                        const disableAdd = hasAnyPlaced && !isEditing;
                        const showAddState = isEditing || !disableAdd;
                        return (
                          <div
                            key={type.id}
                            className="lid-module-picker-row flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1 justify-start text-left">
                              <span className="w-4 h-4 rounded shrink-0" style={{ backgroundColor: swatchColor }} />
                              <span className="text-sm text-gray-900 truncate">{type.name}</span>
                              <span className="lid-module-btn-dim text-gray-500 shrink-0 text-xs">
                                {type.widthFt} ft. × {type.heightFt} ft.
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 shrink-0">
                              <label className="flex items-center text-xs text-gray-600 whitespace-nowrap leading-none">
                                Qty
                              </label>
                              <input
                                type="number"
                                min={0}
                                max={50}
                                value={isEditing ? draftQtyRaw : qtyBaseRaw}
                                onFocus={() => {
                                  // Seed the edit draft from what is already on the plan (or 0 if none).
                                  setPickerQtyDraft((prev) => ({
                                    ...prev,
                                    [type.id]: qtyBaseRaw,
                                  }));
                                  setPickerQtyEditing((prev) => ({ ...prev, [type.id]: true }));
                                }}
                                onChange={(e) => {
                                  setPickerQtyDraft((prev) => ({ ...prev, [type.id]: e.target.value }));
                                }}
                                className="lid-module-qty-input"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (!qtyIsValid) return;
                                  addModule(type.id, qtyForAdd, swatchColor);
                                  // Return to “Added” state after placing.
                                  setPickerQtyEditing((prev) => ({ ...prev, [type.id]: false }));
                                  setPickerQtyDraft((prev) => ({ ...prev, [type.id]: 0 }));
                                }}
                                disabled={disableAdd}
                                className="lid-panel-btn lid-module-qty-add-btn rounded-lg text-sm"
                              >
                                {showAddState ? 'Add' : 'Added'}
                              </button>
                            </div>
                          </div>
                        );
                      }
                      const alreadyPlaced = placedOfType > 0;
                      return (
                        <div
                          key={type.id}
                          className="lid-module-picker-row flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1 justify-start text-left">
                            <span className="w-4 h-4 rounded shrink-0" style={{ backgroundColor: swatchColor }} />
                            <span className="text-sm text-gray-900 truncate">{type.name}</span>
                            <span className="lid-module-btn-dim text-gray-500 shrink-0 text-xs">
                              {type.widthFt} ft. × {type.heightFt} ft.
                            </span>
                          </div>
                          <button
                            type="button"
                            disabled={alreadyPlaced}
                            onClick={() => addModule(type.id, 1, swatchColor)}
                            className="lid-panel-btn lid-module-qty-add-btn rounded-lg text-sm shrink-0"
                          >
                            {alreadyPlaced ? 'Added' : 'Add'}
                          </button>
                        </div>
                      );
                    };

                    const renderRoofSizedRainGardenStripe = (stripeSwatch) => (
                      <div className="lid-module-picker-row w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 justify-start text-left">
                            <span
                              className="h-4 w-4 shrink-0 rounded"
                              style={{ backgroundColor: stripeSwatch }}
                            />
                            <span className="text-sm text-gray-900">Rain Garden</span>
                            <span className="lid-module-btn-dim shrink-0 text-xs text-gray-500">
                              Minimum {Math.round(roofRgMinArea).toLocaleString()} sq ft for roof runoff
                              {rgPlaced
                                ? ` · On plan ${Math.round(rgPlaced.widthFt)} × ${Math.round(
                                    rgPlaced.heightFt
                                  )} ft (${Math.round(rgPlaced.widthFt * rgPlaced.heightFt).toLocaleString()} sq ft)`
                                : ''}
                            </span>
                          </div>
                          <div className="shrink-0">
                            {rgPlaced ? (
                              <button
                                type="button"
                                disabled
                                className="lid-panel-btn lid-module-qty-add-btn rounded-lg text-sm"
                              >
                                Added
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => ensureRainGardenMinSize(roofRgMinArea, stripeSwatch)}
                                className="lid-panel-btn lid-module-qty-add-btn rounded-lg text-sm"
                              >
                                Add
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );

                    return (
                      <>
                        {showRoofRainGardenRow && !roofRgSectionInList && (
                          <div className="flex flex-col w-full">
                            <h3
                              className="lid-sidebar-heading-bar"
                              data-goal="stormwater-management"
                            >
                              Stormwater Management
                            </h3>
                            <div className="flex flex-col gap-2 w-full">
                              {renderRoofSizedRainGardenStripe(
                                goalPickerSwatchColor('stormwater-management', 0, 1)
                              )}
                            </div>
                          </div>
                        )}
                        {sections.map((section) => {
                          const types = section.modules;
                          const paletteGoal = pickerPaletteGoalIdForSection(section.id);
                          const showStripeHere =
                            showRoofRainGardenRow && roofRgSectionId === section.id;
                          const sectionModuleCount = Math.max(
                            1,
                            types.length + (showStripeHere ? 1 : 0)
                          );
                          const stripeSwatch = showStripeHere
                            ? goalPickerSwatchColor(paletteGoal, 0, sectionModuleCount)
                            : null;
                          return (
                            <div key={section.id} className="flex flex-col w-full">
                              <h3 className="lid-sidebar-heading-bar" data-goal={section.id}>
                                {section.label}
                              </h3>
                              <div className="flex flex-col gap-2 w-full">
                                {showStripeHere ? renderRoofSizedRainGardenStripe(stripeSwatch) : null}
                                {types.map((t, idx) =>
                                  renderTypeRow(
                                    t,
                                    goalPickerSwatchColor(
                                      paletteGoal,
                                      showStripeHere ? idx + 1 : idx,
                                      sectionModuleCount
                                    )
                                  )
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </div>

              {modules.length > 0 && (
                <>
                  <div className="mb-4">
                    <h3 className="text-3xl font-semibold text-gray-800 mb-2 uppercase">
                      Calculating your eco-impact
                    </h3>
                    <p className="m-0 mb-2 font-semibold text-gray-700">
                      Modules ({modules.length})
                    </p>
                    <div className="space-y-2 bg-gray-50 pl-2 pr-[3px] pt-2 pb-[3px] rounded max-h-32 overflow-y-auto text-gray-700">
                      {modules.map((m) => (
                        <div key={m.id} className="flex justify-between gap-2 py-0.5">
                          <div className="flex min-w-0 items-center">
                            <div
                              className="w-3 h-3 shrink-0 rounded mr-2"
                              style={{ backgroundColor: getPlanBlockFillColor(m) }}
                            />
                            <span className="min-w-0">{m.name}</span>
                          </div>
                          <span className="shrink-0 text-gray-500">
                            {Math.round(m.widthFt)} ft. × {Math.round(m.heightFt)} ft. (
                            {Math.round(m.widthFt * m.heightFt)} sq ft.)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Ecological Benefits</h3>
                    <div className="space-y-2 text-gray-700">
                      <p className="m-0">
                        <strong>Stormwater Infiltration:</strong>{' '}
                        {Math.round(benefits.stormwater).toLocaleString()} gal/yr
                      </p>
                      <p className="m-0">
                        <strong>
                          Carbon Sequestration (CO<sub>2</sub>):
                        </strong>{' '}
                        {Math.round(benefits.co2).toLocaleString()} lbs/yr
                      </p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      Materials & Rain Garden Plug Calculations
                    </h3>
                    <p className="m-0 text-gray-600">
                      Materials and plant calculations update as you change the size and/or number of modules in your
                      plan. Average spacing for rain garden plants is 1 plug per 1 sf. Trees and shrubs are not
                      included in the rain garden calculation, but you can add rain garden-friendly trees and shrubs if
                      you like, you will just need to adjust the number of herbaceous plugs accordingly.
                      <br />
                      <br />
                      * Plug spacing and materials quantities vary by species and design; this is a planning estimate
                      only.
                    </p>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      Materials List
                    </h3>
                    <div className="space-y-3 bg-gray-50 pl-3 pr-[3px] pt-3 pb-[3px] rounded max-h-96 overflow-y-auto text-gray-700">
                      {modulesMaterials.map((mod, idx) => (
                        <div key={mod.id} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                          <div className="mb-2 font-semibold text-gray-800 text-[14px] leading-tight">
                            {idx + 1}. {mod.name} ({mod.dimensions})
                          </div>
                          <div className="space-y-1.5 pl-2">
                            {Object.entries(mod.materials).map(([mat, qty]) => (
                              <div key={mat} className="flex justify-between gap-2 text-gray-600">
                                <span>{mat}</span>
                                <span className="font-medium">{qty}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="flex flex-row flex-wrap items-center justify-start gap-2 w-full">
                <span className="text-sm font-bold text-gray-700">Page Size:</span>
                <select
                  value={pdfPaperSize}
                  onChange={(e) => setPdfPaperSize(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  aria-label="PDF paper size"
                >
                  <option value="letter">8.5 × 11</option>
                  <option value="tabloid">11 × 17</option>
                </select>
                <button
                  type="button"
                  onClick={exportPDF}
                  disabled={modules.length === 0}
                  className="lid-panel-btn lid-export-pdf-btn ml-[5px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4 shrink-0" />
                  Export PDF
                </button>
                {/* Export JSON hidden for public UI */}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Draggable Divider — wide hit area so resize works beside long sidebar content */}
      <div
        role="separator"
        aria-orientation="vertical"
        className="flex w-3 shrink-0 cursor-col-resize select-none justify-center self-stretch px-1"
        onMouseDown={handleDividerMouseDown}
        title="Drag to resize sidebar"
      >
        <div
          className={`h-full w-1 shrink-0 rounded-full ${isDragging ? 'bg-gray-500' : 'bg-gray-300 hover:bg-gray-400'}`}
        />
      </div>

      <div className="flex-1 min-h-0 flex flex-col p-2 overflow-hidden">
        {showSitePlanPanel ? (
          <div className="bg-white rounded-lg px-2 pb-2 pt-0 flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="mb-1 flex-shrink-0">
              {/* One meta grid; md:mt-8 drops all four lines so row 2 (Area / On Screen) lines up with scroll hint. */}
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-x-6 md:gap-y-0">
                <div className="flex min-w-0 flex-1 flex-col gap-4">
                  <h1 className="text-4xl font-bold leading-tight text-gray-800 min-w-0 mb-0 mt-0 pr-2">
                    {sitePlanTitleText}
                  </h1>
                  <div className="lid-site-plan-header-meta grid min-w-0 grid-cols-1 gap-x-10 gap-y-2 text-gray-600 sm:grid-cols-2 md:mt-8">
                    <div>
                      <strong className="font-semibold text-gray-800">Site Dimensions:</strong>{' '}
                      {siteWidth || '0'} ft. × {siteLength || '0'} ft.
                    </div>
                    <div>
                      <strong className="font-semibold text-gray-800">Base Scale:</strong> 1 ft ={' '}
                      {PLAN_PX_PER_FOOT} pixels
                    </div>
                    <div>
                      <strong className="font-semibold text-gray-800">Area:</strong>{' '}
                      {((Number(siteWidth) || 0) * (Number(siteLength) || 0)).toLocaleString()} sq ft.
                    </div>
                    <div>
                      <strong className="font-semibold text-gray-800">On Screen Now:</strong>{' '}
                      {Math.round(clampPlanZoom(planViewZoom) * 100)}%
                    </div>
                  </div>
                </div>
                <div
                  className="lid-site-plan-header-meta lid-plan-zoom-tools w-max max-w-full min-w-[min(100%,27.5rem)] shrink-0 overflow-x-auto px-4 py-0 text-left text-black"
                  aria-label="Pan and zoom tools"
                >
                  <h3 className="mb-3 mt-0 text-xl font-semibold text-black text-left">
                    Pan &amp; Zoom Tools
                  </h3>
                  <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-2">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        title="Zoom out"
                        className="lid-plan-zoom-btn"
                        disabled={clampPlanZoom(planViewZoom) <= PLAN_ZOOM_MIN}
                        onClick={() => setPlanViewZoom((z) => clampPlanZoom(z - PLAN_ZOOM_STEP))}
                      >
                        <span className="lid-plan-zoom-btn-label" aria-hidden>
                          −
                        </span>
                      </button>
                      <span className="min-w-[3rem] text-center text-sm tabular-nums text-black">
                        {Math.round(clampPlanZoom(planViewZoom) * 100)}%
                      </span>
                      <button
                        type="button"
                        title="Zoom in"
                        className="lid-plan-zoom-btn"
                        disabled={clampPlanZoom(planViewZoom) >= PLAN_ZOOM_MAX}
                        onClick={() => setPlanViewZoom((z) => clampPlanZoom(z + PLAN_ZOOM_STEP))}
                      >
                        <span className="lid-plan-zoom-btn-label" aria-hidden>
                          +
                        </span>
                      </button>
                    </div>
                    <button
                      type="button"
                      className="lid-plan-zoom-reset rounded-lg shrink-0"
                      onClick={() => setPlanViewZoom(1)}
                    >
                      Reset
                    </button>
                  </div>
                  <p className="lid-plan-zoom-box-hint mb-0 mt-2 text-left text-black">
                    Hold Ctrl (or ⌘) and scroll over the plan to zoom.
                  </p>
                  <p className="lid-plan-zoom-box-hint mb-0 mt-2 text-left text-black">
                    Scroll the plan panel if the lot is larger than the window.
                  </p>
                </div>
              </div>
            </div>

            <div
              ref={planOuterRef}
              className="flex-1 min-h-[200px] mt-6 overflow-auto rounded-md border border-gray-200 bg-gray-50/40"
            >
              <div className="min-h-full w-full flex items-start justify-center px-1 pt-4 pb-0 box-border">
              <div className="inline-flex shrink-0 items-start gap-[10px]">
                {/* mt-10 matches plan wrapper pt-10 so LEGEND top aligns with the lot rectangle (not the padded area above for REAR LOT LINE) */}
                <div className="flex flex-col items-start gap-6 shrink-0 mt-10">
                  <PlanLegend />
                  {modules.length > 0 && <PlanModuleToolTips />}
                </div>
                <div
                  className="inline-block shrink-0"
                  style={{
                    transform: `rotate(${planRotationDeg}deg)`,
                    WebkitTransform: `rotate(${planRotationDeg}deg)`,
                    transformOrigin: '50% 50%',
                    WebkitTransformOrigin: '50% 50%',
                  }}
                >
                {/* Padding reserves space for dimension labels (absolute; outside plan box height) so they are not clipped */}
                <div className="inline-block shrink-0 box-border pt-10 pb-2.5">
                <div className="relative shrink-0" style={getPlanBoxStyle()}>
                  {(Number(siteWidth) || 0) > 0 && (Number(siteLength) || 0) > 0 && (
                    <>
                      <div className="lid-plan-dimension-label absolute left-1/2 bottom-full z-20 mb-2 -translate-x-1/2 text-center pointer-events-none">
                        REAR LOT LINE {siteWidth} ft.
                      </div>
                      <div className="lid-plan-dimension-label absolute left-1/2 top-full z-20 mt-2 -translate-x-1/2 text-center pointer-events-none">
                        FRONTAGE {siteWidth} ft.
                      </div>
                      <div
                        className="lid-plan-dimension-label absolute top-1/2 left-full z-20 pointer-events-none whitespace-nowrap"
                        style={{
                          left: 'calc(100% + 0.5rem - 80px)',
                          /* Keep the wrapper vertically centered; rotate happens on inner span */
                          transform: 'translateY(calc(-50% - 40px))',
                          transformOrigin: 'center',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            transform: 'rotate(-90deg)',
                            transformOrigin: 'right center',
                          }}
                        >
                          DEPTH {siteLength} ft.
                        </span>
                      </div>
                    </>
                  )}
                  <div
                    className="absolute inset-0 box-border"
                    data-lid-plan-capture=""
                    ref={(el) => {
                      planInnerRef.current = el;
                      canvasRef.current = el;
                      if (!el) return;
                      if (el._resizeObs) {
                        el._resizeObs.disconnect();
                      }
                      const ro = new ResizeObserver(() => {
                        setParcelRenderTick(t => t + 1);
                      });
                      ro.observe(el);
                      el._resizeObs = ro;
                    }}
                  >
                {/* Basemap layer removed */}

                {/* Module tips: see PlanModuleToolTips under LEGEND */}

                {/* north arrow removed */}
              {siteCreated && modules.map(module => {
                const widthPercent = ((module.widthFt / (Number(siteWidth) || 1)) * 100);
                const heightPercent = ((module.heightFt / (Number(siteLength) || 1)) * 100);
                const isSelected = selectedModule === module.id;
                const showPlanFootprintDims =
                  Math.round(module.widthFt) >= 10 && Math.round(module.heightFt) >= 10;
                const planEllipseTree = ROUND_PLAN_MODULE_TYPES.has(module.type);
                const planEllipseRainGarden = ELLIPSE_PLAN_MODULE_TYPES.has(module.type);

                return (
                  <div
                    key={module.id}
                    className={`absolute border-[0.25px] border-solid transition-shadow ${
                      isSelected ? 'border-blue-600 shadow-lg' : 
                      module.isRoofArea ? 'border-gray-500 border-dashed' : 'border-gray-700'
                    }${planEllipseTree || planEllipseRainGarden ? ' overflow-hidden' : ''}${
                      planEllipseTree ? ' rounded-full' : ''
                    }`}
                    style={{
                      zIndex: planModuleStackZIndex(module.type, isSelected),
                      left: `${module.xPercent}%`,
                      top: `${module.yPercent}%`,
                      width: `${widthPercent}%`,
                      height: `${heightPercent}%`,
                      borderWidth:
                        module.type === 'shade-tree'
                          ? '2px'
                          : module.type === 'fruit-tree'
                            ? '1px'
                            : '0.25px',
                      backgroundColor: getPlanBlockFillColor(module),
                      opacity:
                        module.isRoofArea || TREE_PLAN_MODULE_TYPES.has(module.type) ? 0.7 : 1,
                      backgroundImage: module.isRoofArea ? 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 20px)' : 'none',
                      transform: `rotate(${module.rotation}deg)`,
                      transformOrigin: 'center',
                      ...(planEllipseRainGarden ? { borderRadius: '50%' } : {}),
                    }}
                  >
                    <div
                      onMouseDown={(e) => handleModuleMouseDown(e, module)}
                      className="absolute inset-2 cursor-move flex flex-col items-center justify-center text-xs font-semibold text-gray-900"
                      style={{
                        transform: `rotate(${-module.rotation - planRotationDeg}deg)`,
                        transformOrigin: 'center'
                      }}
                    >
                      <div className="text-center text-sm leading-tight bg-white bg-opacity-80 px-1 rounded">
                        {module.name}
                      </div>
                      {showPlanFootprintDims && (
                        <div className="text-xs mt-1 bg-white bg-opacity-80 px-1 rounded">
                          {Math.round(module.widthFt)} ft. × {Math.round(module.heightFt)} ft.
                        </div>
                      )}
                      {module.isRoofArea && showPlanFootprintDims && (
                        <div className="text-xs mt-1 bg-gray-100 text-gray-800 px-1 rounded">
                          {roofAreaSqFt} sq ft.
                        </div>
                      )}
                    </div>

                    {isSelected && !module.isRoofArea && !module.fixedFootprint && (
                      <>
                        <div
                          onMouseDown={(e) => handleResizeMouseDown(e, module, 'nw')}
                          className="absolute -top-1 -left-1 w-3 h-3 bg-blue-600 border border-white cursor-nw-resize z-20"
                        />
                        <div
                          onMouseDown={(e) => handleResizeMouseDown(e, module, 'ne')}
                          className="absolute -top-1 -right-1 w-3 h-3 bg-blue-600 border border-white cursor-ne-resize z-20"
                        />
                        <div
                          onMouseDown={(e) => handleResizeMouseDown(e, module, 'sw')}
                          className="absolute -bottom-1 -left-1 w-3 h-3 bg-blue-600 border border-white cursor-sw-resize z-20"
                        />
                        <div
                          onMouseDown={(e) => handleResizeMouseDown(e, module, 'se')}
                          className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-600 border border-white cursor-se-resize z-20"
                        />
                        <div
                          onMouseDown={(e) => handleResizeMouseDown(e, module, 'n')}
                          className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-600 border border-white cursor-n-resize z-20"
                        />
                        <div
                          onMouseDown={(e) => handleResizeMouseDown(e, module, 's')}
                          className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-600 border border-white cursor-s-resize z-20"
                        />
                        <div
                          onMouseDown={(e) => handleResizeMouseDown(e, module, 'w')}
                          className="absolute -left-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-600 border border-white cursor-w-resize z-20"
                        />
                        <div
                          onMouseDown={(e) => handleResizeMouseDown(e, module, 'e')}
                          className="absolute -right-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-600 border border-white cursor-e-resize z-20"
                        />
                      </>
                    )}
                    {isSelected && !module.isRoofArea && (
                      <>
                        <button
                          type="button"
                          onMouseDown={(e) => handleRotateMouseDown(e, module, 'module')}
                          className="absolute -top-3 -left-8 bg-green-500 text-white rounded-full p-1 hover:bg-green-600 shadow-lg z-20 cursor-grab active:cursor-grabbing select-none"
                          title="Drag to rotate freely"
                        >
                          <RotateCw className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteModule(module.id);
                          }}
                          className="absolute -bottom-3 -right-3 lid-delete-btn bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-lg z-20"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
              
              {/* Existing Structures */}
              {siteCreated && existingStructures.map(structure => {
                const widthPercent = (structure.widthFt / (Number(siteWidth) || 1)) * 100;
                const heightPercent = (structure.heightFt / (Number(siteLength) || 1)) * 100;
                const isSelected = selectedModule === structure.id;
                const showPlanFootprintDims =
                  Math.round(structure.widthFt) >= 10 && Math.round(structure.heightFt) >= 10;
                
                return (
                  <div
                    key={structure.id}
                    className={`absolute border-2 border-dashed transition-shadow ${
                      isSelected ? 'border-blue-600 shadow-lg' : 'border-gray-500'
                    }`}
                    style={{
                      zIndex: isSelected ? 6 : 5,
                      left: `${structure.xPercent}%`,
                      top: `${structure.yPercent}%`,
                      width: `${widthPercent}%`,
                      height: `${heightPercent}%`,
                      backgroundColor: '#f3f4f6',
                      opacity: 0.7,
                      backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 20px)',
                      transform: `rotate(${structure.rotation}deg)`,
                      transformOrigin: 'center'
                    }}
                  >
                    <div
                      onMouseDown={(e) => handleModuleMouseDown(e, structure)}
                      className="absolute inset-2 cursor-move flex flex-col items-center justify-center text-xs font-semibold text-gray-900"
                      style={{
                        transform: `rotate(${-structure.rotation - planRotationDeg}deg)`,
                        transformOrigin: 'center'
                      }}
                    >
                      <div className="text-center leading-tight">{structure.name}</div>
                      {showPlanFootprintDims && (
                        <>
                          <div className="text-xs mt-1 bg-white bg-opacity-80 px-1 rounded">
                            {Math.round(structure.widthFt)} ft. × {Math.round(structure.heightFt)} ft.
                          </div>
                          <div className="text-xs mt-1 bg-gray-100 text-gray-800 px-1 rounded">
                            {Math.round(structure.widthFt * structure.heightFt).toLocaleString()} sq ft.
                          </div>
                        </>
                      )}
                    </div>

                    {isSelected && (
                      <button
                        onMouseDown={(e) => handleRotateMouseDown(e, structure, 'structure')}
                        className="absolute -top-3 -left-8 bg-green-500 text-white rounded-full p-1 hover:bg-green-600 shadow-lg z-20 cursor-grab active:cursor-grabbing select-none"
                        title="Drag to rotate freely"
                      >
                        <RotateCw className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
              
              {siteCreated && modules.length === 0 && existingStructures.length === 0 && (
                <div className="absolute inset-0 z-0 flex items-center justify-center text-gray-400 pointer-events-none">
                  <div className="text-center">
                    <Grid className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Click modules to place elements in your site plan</p>
                  </div>
                </div>
              )}

                {/* Parcel + building footprints: render last so strokes sit above module fills (canvas is pointer-events-none). */}
                <canvas
                  ref={parcelCanvasRef}
                  className="absolute inset-0 z-[35]"
                  style={{ backgroundColor: 'transparent', pointerEvents: 'none' }}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
                <svg
                  className="pointer-events-none absolute inset-0 z-[36] h-full w-full overflow-visible"
                  aria-hidden
                >
                  <PlanSetbackRect siteWidth={siteWidth} siteLength={siteLength} />
                  <rect
                    x="1"
                    y="1"
                    width="calc(100% - 2px)"
                    height="calc(100% - 2px)"
                    fill="none"
                    stroke="#1f2937"
                    strokeWidth="2"
                    strokeDasharray={PLAN_BOUNDARY_DASH.join(' ')}
                  />
                </svg>
                  </div>
                </div>
                </div>
                </div>
              </div>
              </div>
            </div>

            {/* Tips moved to top of canvas and shown only when modules are present */}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            {!parcelData ? (
              <div className="text-center text-gray-500">
                <img
                  src="/images/Gardening_Icon.png"
                  alt="Gardening icon"
                  className="w-24 h-24 mx-auto mb-3 opacity-70"
                />
                <p className="text-lg font-semibold">Your site plan will display here</p>
              </div>
            ) : (
              <div className="h-full" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LIDSitePlanner;